import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { wishlistService } from '../../services/wishlistService';
import { getUserAlerts, toggleAlert, deleteAlert, createAlert } from '../../services/propertyAlertsService';
import {
  alertSuspiciousLogin,
  alertSessionTerminated,
  alertFailedVerification,
} from '../../services/securityAlertsService';
import {
  trackMonEspaceTabSwitch,
  trackAlertCreated,
  trackAlertToggle,
  trackWishlistToPropertyFunnel,
} from '../../services/monEspaceAnalyticsService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (price) => {
  if (!price) return 'Prix sur demande';
  if (price >= 1000000) return `${(price / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M XOF`;
  if (price >= 1000) return `${(price / 1000)?.toFixed(0)}K XOF`;
  return `${price?.toLocaleString()} XOF`;
};

const formatDate = (dateStr, withTime = false) => {
  if (!dateStr) return '—';
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
  return new Date(dateStr)?.toLocaleDateString('fr-FR', opts);
};

const statusColors = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-100',
  confirmed: 'bg-green-50 text-green-700 border-green-100',
  completed: 'bg-surface text-text-secondary border-border',
  cancelled: 'bg-red-50 text-red-600 border-red-100',
  rescheduled: 'bg-amber-50 text-amber-700 border-amber-100',
};

const statusLabels = {
  scheduled: 'Planifié',
  confirmed: 'Confirmé',
  completed: 'Terminé',
  cancelled: 'Annulé',
  rescheduled: 'Reprogrammé',
};

// ─── Saved Properties Tab ─────────────────────────────────────────────────────

const SavedPropertiesTab = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await wishlistService?.getWishlist(user?.id, {});
      setItems(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = async (propertyId) => {
    setRemoving(propertyId);
    try {
      await wishlistService?.removeFromWishlist(user?.id, propertyId);
      setItems(prev => prev?.filter(i => i?.property_id !== propertyId));
    } finally {
      setRemoving(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (items?.length === 0) return (
    <div className="text-center py-16">
      <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
        <Icon name="Heart" size={26} className="text-rose-400" />
      </div>
      <p className="text-ios-callout font-body-medium text-text-primary mb-1.5">Aucune propriété sauvegardée</p>
      <p className="text-ios-caption1 text-text-secondary mb-5">Cliquez sur le cœur d'une propriété pour la retrouver ici.</p>
      <Link to="/property-search-listings" className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-all shadow-ios-light">
        <Icon name="Search" size={15} color="white" />
        <span>Explorer les propriétés</span>
      </Link>
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-ios-caption1 text-text-secondary mb-4">{items?.length} propriété{items?.length !== 1 ? 's' : ''} sauvegardée{items?.length !== 1 ? 's' : ''}</p>
      {items?.map(item => (
        <div key={item?.id} className="flex items-center gap-3 p-3.5 rounded-2xl border border-border/50 bg-white hover:shadow-ios-light transition-all duration-200">
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-surface">
            {item?.property_image ? (
              <img src={item?.property_image} alt={item?.property_title || 'Propriété'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="Home" size={20} className="text-text-secondary" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-ios-callout font-body-semibold text-text-primary truncate">{item?.property_title || 'Propriété sans titre'}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {item?.property_commune && (
                <span className="flex items-center gap-0.5 text-ios-caption1 text-text-secondary">
                  <Icon name="MapPin" size={11} />
                  {item?.property_commune}
                </span>
              )}
              <span className="text-ios-caption1 font-body-semibold text-brand-earth">{formatPrice(item?.property_price)}</span>
            </div>
            <p className="text-[10px] text-text-secondary mt-0.5">Ajouté le {formatDate(item?.created_at)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to={`/individual-property-page/${item?.property_id}`}
              onClick={() => trackWishlistToPropertyFunnel(item?.property_id)}
              className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center hover:bg-brand-earth/20 transition-all" title="Voir">
              <Icon name="ExternalLink" size={15} className="text-brand-earth" />
            </Link>
            <button onClick={() => handleRemove(item?.property_id)} disabled={removing === item?.property_id} className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center hover:bg-rose-100 transition-all disabled:opacity-50" title="Retirer">
              {removing === item?.property_id ? (
                <div className="w-3.5 h-3.5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="HeartOff" size={15} className="text-rose-500" />
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Alert Subscriptions Tab ──────────────────────────────────────────────────

const PROPERTY_TYPES = ['apartment', 'house', 'land', 'commercial', 'villa'];
const PROPERTY_TYPE_LABELS = { apartment: 'Appartement', house: 'Maison', land: 'Terrain', commercial: 'Commercial', villa: 'Villa' };
const COMMUNES_LIST = ['Cocody', 'Plateau', 'Marcory', 'Treichville', 'Adjamé', 'Abobo', 'Yopougon', 'Koumassi', 'Port-Bouët', 'Attécoubé', 'Bingerville', 'Songon'];

const AlertsTab = ({ user }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [newAlert, setNewAlert] = useState({
    name: '',
    commune_names: [],
    property_types: [],
    market_type: 'both',
    min_price: '',
    max_price: '',
    notify_email: true,
    notify_sms: false,
  });

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    const { data } = await getUserAlerts(user?.id);
    setAlerts(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (alertId, current) => {
    setToggling(alertId);
    await toggleAlert(alertId, !current);
    trackAlertToggle(!current);
    setAlerts(prev => prev?.map(a => a?.id === alertId ? { ...a, is_active: !current } : a));
    setToggling(null);
  };

  const handleDelete = async (alertId) => {
    setDeleting(alertId);
    await deleteAlert(alertId);
    setAlerts(prev => prev?.filter(a => a?.id !== alertId));
    setDeleting(null);
  };

  const handleCreateAlert = async (e) => {
    e?.preventDefault();
    if (!newAlert?.name?.trim()) { setFormError('Donnez un nom à votre alerte.'); return; }
    setSaving(true);
    setFormError('');
    const { error } = await createAlert(user?.id, {
      ...newAlert,
      min_price: newAlert?.min_price ? Number(newAlert?.min_price) : null,
      max_price: newAlert?.max_price ? Number(newAlert?.max_price) : null,
    });
    if (error) { setFormError(error); setSaving(false); return; }
    trackAlertCreated(newAlert);
    await load();
    setShowForm(false);
    setSaving(false);
    setNewAlert({ name: '', commune_names: [], property_types: [], market_type: 'both', min_price: '', max_price: '', notify_email: true, notify_sms: false });
  };

  const toggleCommune = (c) => setNewAlert(a => ({
    ...a,
    commune_names: a?.commune_names?.includes(c) ? a?.commune_names?.filter(x => x !== c) : [...a?.commune_names, c],
  }));

  const toggleType = (t) => setNewAlert(a => ({
    ...a,
    property_types: a?.property_types?.includes(t) ? a?.property_types?.filter(x => x !== t) : [...a?.property_types, t],
  }));

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-ios-caption1 text-text-secondary">{alerts?.length} alerte{alerts?.length !== 1 ? 's' : ''} configurée{alerts?.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center space-x-1.5 px-4 py-2 bg-brand-earth text-white rounded-xl text-ios-caption1 font-body-semibold hover:bg-brand-earth/90 transition-all shadow-ios-light"
        >
          <Icon name={showForm ? 'X' : 'Plus'} size={13} color="white" />
          <span>{showForm ? 'Annuler' : 'Nouvelle alerte'}</span>
        </button>
      </div>
      {/* Create Alert Form */}
      {showForm && (
        <form onSubmit={handleCreateAlert} className="bg-brand-gold/5 border border-brand-gold/30 rounded-2xl p-5 space-y-4">
          <h3 className="text-ios-callout font-body-semibold text-brand-earth">Créer une alerte</h3>

          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Nom de l'alerte *</label>
            <input
              type="text"
              value={newAlert?.name}
              onChange={e => setNewAlert(a => ({ ...a, name: e?.target?.value }))}
              placeholder="Ex: Appartement Cocody"
              className="w-full px-4 py-2.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-2">Communes</label>
            <div className="flex flex-wrap gap-2">
              {COMMUNES_LIST?.map(c => (
                <button key={c} type="button" onClick={() => toggleCommune(c)}
                  className={`px-3 py-1.5 rounded-xl text-ios-caption1 font-body-medium border transition-all ${newAlert?.commune_names?.includes(c) ? 'bg-brand-earth text-white border-brand-earth' : 'bg-white text-text-secondary border-border hover:border-brand-gold/50'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-2">Types de propriété</label>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPES?.map(t => (
                <button key={t} type="button" onClick={() => toggleType(t)}
                  className={`px-3 py-1.5 rounded-xl text-ios-caption1 font-body-medium border transition-all ${newAlert?.property_types?.includes(t) ? 'bg-brand-earth text-white border-brand-earth' : 'bg-white text-text-secondary border-border hover:border-brand-gold/50'}`}>
                  {PROPERTY_TYPE_LABELS?.[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Prix min (XOF)</label>
              <input type="number" value={newAlert?.min_price} onChange={e => setNewAlert(a => ({ ...a, min_price: e?.target?.value }))} placeholder="0"
                className="w-full px-3 py-2.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all" />
            </div>
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Prix max (XOF)</label>
              <input type="number" value={newAlert?.max_price} onChange={e => setNewAlert(a => ({ ...a, max_price: e?.target?.value }))} placeholder="Illimité"
                className="w-full px-3 py-2.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Type de marché</label>
              <select value={newAlert?.market_type} onChange={e => setNewAlert(a => ({ ...a, market_type: e?.target?.value }))}
                className="w-full px-3 py-2.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white transition-all">
                <option value="both">Vente & Location</option>
                <option value="sale">Vente uniquement</option>
                <option value="rental">Location uniquement</option>
              </select>
            </div>
            <div className="flex flex-col justify-end space-y-2 pb-0.5">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={newAlert?.notify_email} onChange={e => setNewAlert(a => ({ ...a, notify_email: e?.target?.checked }))} className="w-4 h-4 rounded border-border text-brand-earth focus:ring-brand-gold" />
                <span className="text-ios-caption1 font-body-medium text-text-primary">Email</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={newAlert?.notify_sms} onChange={e => setNewAlert(a => ({ ...a, notify_sms: e?.target?.checked }))} className="w-4 h-4 rounded border-border text-brand-earth focus:ring-brand-gold" />
                <span className="text-ios-caption1 font-body-medium text-text-primary">SMS</span>
              </label>
            </div>
          </div>

          {formError && <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100">{formError}</p>}

          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-body-semibold hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-ios-light">
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Icon name="Bell" size={15} color="white" /><span>Créer l'alerte</span></>}
          </button>
        </form>
      )}
      {/* Alerts List */}
      {alerts?.length === 0 && !showForm ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <Icon name="BellOff" size={26} className="text-amber-400" />
          </div>
          <p className="text-ios-callout font-body-medium text-text-primary mb-1.5">Aucune alerte configurée</p>
          <p className="text-ios-caption1 text-text-secondary">Créez une alerte pour être notifié des nouvelles propriétés.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts?.map(alert => (
            <div key={alert?.id} className={`p-4 rounded-2xl border transition-all ${alert?.is_active ? 'bg-white border-border/50 shadow-ios-light' : 'bg-surface/50 border-border/30'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-ios-callout font-body-semibold text-text-primary truncate">{alert?.name}</p>
                    <span className={`flex-shrink-0 text-[10px] font-body-semibold px-2 py-0.5 rounded-full ${alert?.is_active ? 'bg-green-50 text-green-700' : 'bg-surface text-text-secondary'}`}>
                      {alert?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {alert?.commune_names?.length > 0 && alert?.commune_names?.map(c => (
                      <span key={c} className="text-[10px] bg-brand-gold/10 text-brand-earth px-2 py-0.5 rounded-full font-body-medium">{c}</span>
                    ))}
                    {alert?.property_types?.length > 0 && alert?.property_types?.map(t => (
                      <span key={t} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-body-medium">{PROPERTY_TYPE_LABELS?.[t] || t}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-ios-caption1 text-text-secondary">
                    {(alert?.min_price || alert?.max_price) && (
                      <span>{alert?.min_price ? formatPrice(alert?.min_price) : '0'} – {alert?.max_price ? formatPrice(alert?.max_price) : '∞'}</span>
                    )}
                    <span className="flex items-center gap-1">
                      {alert?.notify_email && <Icon name="Mail" size={11} />}
                      {alert?.notify_sms && <Icon name="MessageSquare" size={11} />}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleToggle(alert?.id, alert?.is_active)} disabled={toggling === alert?.id}
                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 ${alert?.is_active ? 'bg-green-50 hover:bg-green-100' : 'bg-surface hover:bg-border/30'}`}>
                    {toggling === alert?.id ? <div className="w-3.5 h-3.5 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" /> : <Icon name={alert?.is_active ? 'BellRing' : 'BellOff'} size={15} className={alert?.is_active ? 'text-green-600' : 'text-text-secondary'} />}
                  </button>
                  <button onClick={() => handleDelete(alert?.id)} disabled={deleting === alert?.id}
                    className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center hover:bg-red-100 transition-all disabled:opacity-50">
                    {deleting === alert?.id ? <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /> : <Icon name="Trash2" size={15} className="text-red-500" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Appointment History Tab ──────────────────────────────────────────────────

const AppointmentsTab = ({ user }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('appointments')
          ?.select(`
            *,
            property:properties(id, title, address, property_type),
            notary:notaries(id, notary_name, office_address)
          `)
          ?.eq('client_id', user?.id)
          ?.order('scheduled_date', { ascending: false });
        if (!error) setAppointments(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  const filtered = filter === 'all' ? appointments : appointments?.filter(a => a?.status === filter);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'scheduled', label: 'Planifiés' },
          { key: 'confirmed', label: 'Confirmés' },
          { key: 'completed', label: 'Terminés' },
          { key: 'cancelled', label: 'Annulés' },
        ]?.map(f => (
          <button key={f?.key} onClick={() => setFilter(f?.key)}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-ios-caption1 font-body-medium border transition-all ${filter === f?.key ? 'bg-brand-earth text-white border-brand-earth' : 'bg-white text-text-secondary border-border hover:border-brand-gold/50'}`}>
            {f?.label}
          </button>
        ))}
      </div>
      {filtered?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Icon name="CalendarX" size={26} className="text-blue-400" />
          </div>
          <p className="text-ios-callout font-body-medium text-text-primary mb-1.5">Aucun rendez-vous</p>
          <p className="text-ios-caption1 text-text-secondary mb-5">Vous n'avez pas encore de rendez-vous dans cette catégorie.</p>
          <Link to="/consultation-booking-hub" className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-all shadow-ios-light">
            <Icon name="Calendar" size={15} color="white" />
            <span>Prendre un rendez-vous</span>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered?.map(appt => (
            <div key={appt?.id} className="p-4 rounded-2xl border border-border/50 bg-white shadow-ios-light hover:shadow-ios-medium transition-all duration-200">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-ios-callout font-body-semibold text-text-primary truncate">
                    {appt?.property?.title || appt?.appointment_type || 'Rendez-vous'}
                  </p>
                  {appt?.property?.address && (
                    <p className="text-ios-caption1 text-text-secondary mt-0.5 flex items-center gap-1">
                      <Icon name="MapPin" size={11} />
                      {appt?.property?.address}
                    </p>
                  )}
                </div>
                <span className={`flex-shrink-0 text-[10px] font-body-semibold px-2.5 py-1 rounded-full border ${statusColors?.[appt?.status] || 'bg-surface text-text-secondary border-border'}`}>
                  {statusLabels?.[appt?.status] || appt?.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-ios-caption1 text-text-secondary">
                <div className="flex items-center gap-1.5">
                  <Icon name="Calendar" size={12} className="text-brand-earth flex-shrink-0" />
                  <span>{formatDate(appt?.scheduled_date, true)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Icon name="Clock" size={12} className="text-brand-earth flex-shrink-0" />
                  <span>{appt?.duration_minutes || 60} min</span>
                </div>
                {appt?.notary?.notary_name && (
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Icon name="Scale" size={12} className="text-brand-earth flex-shrink-0" />
                    <span>Notaire : {appt?.notary?.notary_name}</span>
                  </div>
                )}
                {appt?.location && (
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Icon name="Navigation" size={12} className="text-brand-earth flex-shrink-0" />
                    <span className="truncate">{appt?.location}</span>
                  </div>
                )}
              </div>

              {appt?.client_notes && (
                <div className="mt-3 p-3 bg-surface/50 rounded-xl">
                  <p className="text-ios-caption1 text-text-secondary italic">"{appt?.client_notes}"</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Preferences Tab ──────────────────────────────────────────────────────────

const PreferencesTab = ({ user, userProfile, onUpdated }) => {
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    preferredCommune: '',
    preferredPropertyType: '',
    budget: '',
    marketType: 'both',
    language: 'fr',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      fullName: userProfile?.full_name || user?.user_metadata?.full_name || '',
      phone: userProfile?.phone || '',
      preferredCommune: userProfile?.preferred_commune || '',
      preferredPropertyType: userProfile?.preferred_property_type || '',
      budget: userProfile?.budget || '',
      marketType: userProfile?.market_type_preference || 'both',
      language: 'fr',
    });
  }, [userProfile, user]);

  const handleSave = async () => {
    if (!user?.id) return;
    setError('');
    setSaving(true);
    try {
      const { error: err } = await supabase?.from('user_profiles')?.update({
        full_name: form?.fullName?.trim(),
        phone: form?.phone?.trim(),
        updated_at: new Date()?.toISOString(),
      })?.eq('id', user?.id);

      if (err) { setError(err?.message); return; }
      await supabase?.auth?.updateUser({ data: { full_name: form?.fullName?.trim() } });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onUpdated?.();
    } catch {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Info */}
      <div>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <Icon name="User" size={16} className="text-brand-earth" />
          Informations personnelles
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Nom complet</label>
            <input type="text" value={form?.fullName} onChange={e => setForm(f => ({ ...f, fullName: e?.target?.value }))} placeholder="Votre nom complet"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all" />
          </div>
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Email</label>
            <input type="email" value={user?.email || ''} disabled placeholder=""
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system bg-surface/60 text-text-secondary" />
            <p className="text-[10px] text-text-secondary mt-1">L'email ne peut pas être modifié ici.</p>
          </div>
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Téléphone</label>
            <input type="tel" value={form?.phone} onChange={e => setForm(f => ({ ...f, phone: e?.target?.value }))} placeholder="+225 07 00 00 00 00"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all" />
          </div>
        </div>
      </div>
      {/* Search Preferences */}
      <div>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={16} className="text-brand-earth" />
          Préférences de recherche
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Commune préférée</label>
            <select value={form?.preferredCommune} onChange={e => setForm(f => ({ ...f, preferredCommune: e?.target?.value }))}
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white transition-all">
              <option value="">Toutes les communes</option>
              {COMMUNES_LIST?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Type de propriété préféré</label>
            <select value={form?.preferredPropertyType} onChange={e => setForm(f => ({ ...f, preferredPropertyType: e?.target?.value }))}
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white transition-all">
              <option value="">Tous les types</option>
              {PROPERTY_TYPES?.map(t => <option key={t} value={t}>{PROPERTY_TYPE_LABELS?.[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Type de marché</label>
            <select value={form?.marketType} onChange={e => setForm(f => ({ ...f, marketType: e?.target?.value }))}
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white transition-all">
              <option value="both">Vente & Location</option>
              <option value="sale">Vente uniquement</option>
              <option value="rental">Location uniquement</option>
            </select>
          </div>
        </div>
      </div>
      {/* Quick Links */}
      <div>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-3 flex items-center gap-2">
          <Icon name="Settings" size={16} className="text-brand-earth" />
          Paramètres avancés
        </h3>
        <div className="space-y-2">
          {[
            { label: 'Préférences de notifications', sub: 'SMS, email et fréquence', icon: 'Bell', to: '/notification-preferences' },
            { label: 'Paramètres de communication', sub: 'Rappels et messages', icon: 'MessageSquare', to: '/account-communication-settings' },
            { label: 'Sécurité du compte', sub: 'Mot de passe et appareils', icon: 'ShieldCheck', to: '/account-dashboard' },
          ]?.map(item => (
            <Link key={item?.to} to={item?.to} className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-border/50 hover:shadow-ios-light hover:border-brand-gold/30 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-brand-earth/10 flex items-center justify-center">
                  <Icon name={item?.icon} size={15} className="text-brand-earth" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-medium text-text-primary">{item?.label}</p>
                  <p className="text-ios-caption1 text-text-secondary">{item?.sub}</p>
                </div>
              </div>
              <Icon name="ChevronRight" size={15} className="text-text-secondary group-hover:text-brand-earth transition-colors" />
            </Link>
          ))}
        </div>
      </div>
      {error && <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100">{error}</p>}
      <div className="flex items-center justify-end space-x-3 pt-2">
        {saved && (
          <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
            <Icon name="CheckCircle" size={13} />
            <span>Sauvegardé</span>
          </span>
        )}
        <button onClick={handleSave} disabled={saving}
          className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-ios-light">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Icon name="Save" size={15} color="white" /><span>Sauvegarder</span></>}
        </button>
      </div>
    </div>
  );
};

// ─── Security Tab ─────────────────────────────────────────────────────────────

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARN_THRESHOLD_MS = 5 * 60 * 1000; // warn at 5 minutes remaining

const SecurityTab = ({ user }) => {
  // ── Password Change ──
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  // ── Email Verification ──
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const isEmailVerified = !!user?.email_confirmed_at;

  // ── 2FA State ──
  const [twoFASettings, setTwoFASettings] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [twoFAStep, setTwoFAStep] = useState('idle'); // 'idle' | 'choose_method' | 'enter_phone' | 'verify_code' | 'show_backup' | 'disable_confirm'
  const [twoFAMethod, setTwoFAMethod] = useState('email'); // 'email' | 'sms'
  const [twoFAPhone, setTwoFAPhone] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASentCode, setTwoFASentCode] = useState('');
  const [twoFACodeSent, setTwoFACodeSent] = useState(false);
  const [twoFACodeError, setTwoFACodeError] = useState('');
  const [twoFABackupCodes, setTwoFABackupCodes] = useState([]);
  const [twoFAActionLoading, setTwoFAActionLoading] = useState(false);
  const [twoFASuccess, setTwoFASuccess] = useState('');
  const [twoFACooldown, setTwoFACooldown] = useState(0);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // ── MFA Enforcement & Recovery ──
  const [mfaEnforced, setMfaEnforced] = useState(false);
  const [mfaEnforcedLoading, setMfaEnforcedLoading] = useState(false);
  const [mfaEnforcedSuccess, setMfaEnforcedSuccess] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoverySuccess, setRecoverySuccess] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenSuccess, setRegenSuccess] = useState('');
  const [regenConfirm, setRegenConfirm] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState([]);
  const [showNewBackupCodes, setShowNewBackupCodes] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState('idle'); // 'idle' | 'choose' | 'verify_email' | 'verify_sms' | 'success'
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoverySentCode, setRecoverySentCode] = useState('');
  const [recoveryCodeError, setRecoveryCodeError] = useState('');
  const [recoveryActionLoading, setRecoveryActionLoading] = useState(false);
  const [recoveryMethod, setRecoveryMethod] = useState('email');

  // ── Session Timeout ──
  const [idleRemaining, setIdleRemaining] = useState(IDLE_TIMEOUT_MS);
  const [sessionExpired, setSessionExpired] = useState(false);
  const idleStartRef = useRef(Date.now());
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const { signOut } = useAuth();

  // ── Sessions ──
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [sessionExpanded, setSessionExpanded] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [revokeAllConfirm, setRevokeAllConfirm] = useState(false);

  // ── Trusted Devices ──
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [trustingId, setTrustingId] = useState(null);
  const [untrustingId, setUntrustingId] = useState(null);

  // ── History ──
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'success' | 'failed' | 'suspicious'
  const [historyExpanded, setHistoryExpanded] = useState(null);

  // ── Data Export ──
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');

  // ── 2FA Helpers ──
  const generate6DigitCode = () => Math.floor(100000 + Math.random() * 900000)?.toString();

  const generateBackupCodes = () => {
    const codes = [];
    for (let i = 0; i < 8; i++) {
      const part1 = Math.random()?.toString(36)?.substring(2, 6)?.toUpperCase();
      const part2 = Math.random()?.toString(36)?.substring(2, 6)?.toUpperCase();
      codes?.push(`${part1}-${part2}`);
    }
    return codes;
  };

  const load2FASettings = useCallback(async () => {
    if (!user?.id) { setTwoFALoading(false); return; }
    setTwoFALoading(true);
    try {
      const { data } = await supabase
        ?.from('user_2fa_settings')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();
      setTwoFASettings(data || null);
      if (data) {
        setMfaEnforced(data?.mfa_enforced === true);
        setRecoveryEmail(data?.recovery_email || '');
        setRecoveryPhone(data?.recovery_phone || '');
      }
    } catch {
      setTwoFASettings(null);
    } finally {
      setTwoFALoading(false);
    }
  }, [user?.id]);

  const handleSend2FACode = async () => {
    setTwoFAActionLoading(true);
    setTwoFACodeError('');
    const code = generate6DigitCode();
    setTwoFASentCode(code);
    // Simulate sending — in production wire to Twilio SMS or Supabase email
    await new Promise(r => setTimeout(r, 900));
    setTwoFACodeSent(true);
    setTwoFAActionLoading(false);
    setTwoFACooldown(60);
    const interval = setInterval(() => {
      setTwoFACooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerify2FACode = async () => {
    if (twoFACode?.trim() !== twoFASentCode) {
      setTwoFACodeError('Code incorrect. Veuillez réessayer.');
      return;
    }
    setTwoFAActionLoading(true);
    setTwoFACodeError('');
    try {
      const backupCodes = generateBackupCodes();
      setTwoFABackupCodes(backupCodes);
      const payload = {
        user_id: user?.id,
        is_enabled: true,
        method: twoFAMethod,
        phone_number: twoFAMethod === 'sms' ? twoFAPhone : null,
        backup_codes: backupCodes,
        backup_codes_used: [],
        enabled_at: new Date()?.toISOString(),
        last_verified_at: new Date()?.toISOString(),
      };
      const { error } = await supabase
        ?.from('user_2fa_settings')
        ?.upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;
      await load2FASettings();
      setTwoFAStep('show_backup');
      setTwoFASuccess('');
    } catch (err) {
      setTwoFACodeError('Erreur lors de l\'activation. Veuillez réessayer.');
    } finally {
      setTwoFAActionLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setTwoFAActionLoading(true);
    try {
      await supabase
        ?.from('user_2fa_settings')
        ?.update({
          is_enabled: false,
          disabled_at: new Date()?.toISOString(),
        })
        ?.eq('user_id', user?.id);
      await load2FASettings();
      setTwoFAStep('idle');
      setTwoFASuccess('La double authentification a été désactivée.');
      setTimeout(() => setTwoFASuccess(''), 5000);
    } catch {
      // silent
    } finally {
      setTwoFAActionLoading(false);
    }
  };

  const resetTwoFAFlow = () => {
    setTwoFAStep('idle');
    setTwoFACode('');
    setTwoFASentCode('');
    setTwoFACodeSent(false);
    setTwoFACodeError('');
    setTwoFAPhone('');
    setTwoFACooldown(0);
  };

  const is2FAEnabled = twoFASettings?.is_enabled === true;

  // ── MFA Enforcement Toggle ──
  const handleToggleMFAEnforcement = async () => {
    if (!user?.id || !is2FAEnabled) return;
    setMfaEnforcedLoading(true);
    setMfaEnforcedSuccess('');
    const newValue = !mfaEnforced;
    try {
      const { error } = await supabase
        ?.from('user_2fa_settings')
        ?.update({ mfa_enforced: newValue })
        ?.eq('user_id', user?.id);
      if (error) throw error;
      setMfaEnforced(newValue);
      setMfaEnforcedSuccess(newValue
        ? 'Enforcement MFA activé — le défi 2FA sera exigé à chaque connexion.'
        : 'Enforcement MFA désactivé — les appareils de confiance peuvent ignorer le défi 2FA.');
      setTimeout(() => setMfaEnforcedSuccess(''), 5000);
    } catch {
      // silent
    } finally {
      setMfaEnforcedLoading(false);
    }
  };

  // ── Backup Code Regeneration ──
  const handleRegenerateBackupCodes = async () => {
    if (!user?.id || !is2FAEnabled) return;
    setRegenLoading(true);
    setRegenSuccess('');
    try {
      const codes = generateBackupCodes();
      const { error } = await supabase
        ?.from('user_2fa_settings')
        ?.update({
          backup_codes: codes,
          backup_codes_used: [],
          backup_codes_regenerated_at: new Date()?.toISOString(),
        })
        ?.eq('user_id', user?.id);
      if (error) throw error;
      setNewBackupCodes(codes);
      setShowNewBackupCodes(true);
      setRegenConfirm(false);
      setRegenSuccess('Nouveaux codes de secours générés. Sauvegardez-les immédiatement.');
      await load2FASettings();
    } catch {
      // silent
    } finally {
      setRegenLoading(false);
    }
  };

  // ── Recovery Options Save ──
  const handleSaveRecoveryOptions = async () => {
    if (!user?.id) return;
    if (!recoveryEmail?.trim() && !recoveryPhone?.trim()) {
      setRecoveryError('Veuillez saisir au moins un email ou numéro de téléphone de récupération.');
      return;
    }
    setRecoveryLoading(true);
    setRecoveryError('');
    setRecoverySuccess('');
    try {
      const { error } = await supabase
        ?.from('user_2fa_settings')
        ?.update({
          recovery_email: recoveryEmail?.trim() || null,
          recovery_phone: recoveryPhone?.trim() || null,
          recovery_options_set_at: new Date()?.toISOString(),
        })
        ?.eq('user_id', user?.id);
      if (error) throw error;
      setRecoverySuccess('Options de récupération enregistrées avec succès.');
      setShowRecoveryForm(false);
      await load2FASettings();
      setTimeout(() => setRecoverySuccess(''), 5000);
    } catch {
      setRecoveryError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  // ── Session Recovery Flow (post-timeout) ──
  const handleStartSessionRecovery = (method) => {
    setRecoveryMethod(method);
    const code = generate6DigitCode();
    setRecoverySentCode(code);
    setRecoveryStep(method === 'email' ? 'verify_email' : 'verify_sms');
    setRecoveryCode('');
    setRecoveryCodeError('');
  };

  const handleVerifyRecoveryCode = async () => {
    if (recoveryCode?.trim() !== recoverySentCode) {
      setRecoveryCodeError('Code incorrect. Veuillez réessayer.');
      return;
    }
    setRecoveryActionLoading(true);
    await new Promise(r => setTimeout(r, 600));
    setRecoveryStep('success');
    setRecoveryActionLoading(false);
    // Reset session expiry so user can continue
    setSessionExpired(false);
    resetIdleTimer();
  };

  // Detect current device info
  const getDeviceInfo = useCallback(() => {
    const ua = navigator?.userAgent || '';
    let browser = 'Navigateur inconnu';
    let os = 'OS inconnu';
    let deviceType = 'desktop';

    if (/Chrome\//?.test(ua) && !/Chromium|Edg|OPR/?.test(ua)) browser = 'Chrome';
    else if (/Firefox\//?.test(ua)) browser = 'Firefox';
    else if (/Safari\//?.test(ua) && !/Chrome/?.test(ua)) browser = 'Safari';
    else if (/Edg\//?.test(ua)) browser = 'Edge';
    else if (/OPR\//?.test(ua)) browser = 'Opera';

    if (/Windows/?.test(ua)) os = 'Windows';
    else if (/Mac OS X/?.test(ua)) os = 'macOS';
    else if (/Linux/?.test(ua)) os = 'Linux';
    else if (/Android/?.test(ua)) { os = 'Android'; deviceType = 'mobile'; }
    else if (/iPhone|iPad/?.test(ua)) { os = 'iOS'; deviceType = /iPad/?.test(ua) ? 'tablet' : 'mobile'; }

    return { browser, os, deviceType };
  }, []);

  const resetIdleTimer = useCallback(() => {
    idleStartRef.current = Date.now();
    setIdleRemaining(IDLE_TIMEOUT_MS);
    setSessionExpired(false);
  }, []);

  const handleAutoLogout = useCallback(async () => {
    setSessionExpired(true);
    clearInterval(countdownIntervalRef?.current);
    clearTimeout(logoutTimerRef?.current);
    // Send session terminated alert (idle timeout)
    if (user?.id && user?.email) {
      const { browser } = getDeviceInfo();
      await alertSessionTerminated({
        userId: user?.id,
        userEmail: user?.email,
        userName: user?.user_metadata?.full_name || user?.email,
        deviceName: browser ? `${browser}` : undefined,
        reason: 'idle_timeout',
      });
    }
    await signOut();
  }, [signOut, user, getDeviceInfo]);

  const handleExtendSession = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const onActivity = () => resetIdleTimer();
    events?.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    // Countdown tick every second
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - idleStartRef?.current;
      const remaining = Math.max(0, IDLE_TIMEOUT_MS - elapsed);
      setIdleRemaining(remaining);
      if (remaining === 0) {
        handleAutoLogout();
      }
    }, 1000);

    return () => {
      events?.forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(countdownIntervalRef?.current);
      clearTimeout(logoutTimerRef?.current);
    };
  }, [resetIdleTimer, handleAutoLogout]);

  const formatCountdown = (ms) => {
    const totalSec = Math.ceil(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec?.toString()?.padStart(2, '0')}`;
  };

  const isWarning = idleRemaining <= WARN_THRESHOLD_MS && idleRemaining > 0;
  const progressPct = Math.round((idleRemaining / IDLE_TIMEOUT_MS) * 100);

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: user?.email,
        options: {
          emailRedirectTo: `${window?.location?.origin}/user-profile`,
        },
      });
      if (error) {
        setResendError(error?.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
        // Alert: failed verification attempt
        if (user?.id && user?.email) {
          await alertFailedVerification({
            userId: user?.id,
            userEmail: user?.email,
            userName: user?.user_metadata?.full_name || user?.email,
            attemptType: 'email_verification',
            errorMessage: error?.message,
          });
        }
      } else {
        setResendSuccess(true);
        setResendCooldown(60);
        const interval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) { clearInterval(interval); return 0; }
            return prev - 1;
          });
        }, 1000);
        setTimeout(() => setResendSuccess(false), 8000);
      }
    } catch (err) {
      setResendError('Une erreur inattendue est survenue.');
      // Alert: failed verification attempt (unexpected error)
      if (user?.id && user?.email) {
        await alertFailedVerification({
          userId: user?.id,
          userEmail: user?.email,
          userName: user?.user_metadata?.full_name || user?.email,
          attemptType: 'email_verification',
          errorMessage: err?.message || 'Erreur inattendue',
        });
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Load sessions
  const loadSessions = useCallback(async () => {
    if (!user?.id) { setSessionsLoading(false); return; }
    setSessionsLoading(true);
    try {
      const { data } = await supabase
        ?.from('login_sessions')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('last_active_at', { ascending: false })
        ?.limit(10);
      setSessions(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSessionsLoading(false);
    }
  }, [user?.id]);

  // Load trusted devices
  const loadTrustedDevices = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        ?.from('trusted_devices')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('is_active', true)
        ?.order('trusted_at', { ascending: false });
      setTrustedDevices(data || []);
    } catch (err) {
      console.error('loadTrustedDevices error:', err);
    }
  }, [user?.id]);

  // Generate a deterministic fingerprint for a session
  const getDeviceFingerprint = useCallback((session) => {
    const raw = [
      session?.device_type || '',
      session?.browser || '',
      session?.os || '',
      session?.ip_address || '',
    ]?.join('|');
    // Simple hash: btoa of the raw string
    try { return btoa(unescape(encodeURIComponent(raw))); } catch { return raw; }
  }, []);

  // Trust a device
  const handleTrustDevice = async (session) => {
    if (!user?.id) return;
    setTrustingId(session?.id);
    try {
      const fingerprint = getDeviceFingerprint(session);
      const { error } = await supabase
        ?.from('trusted_devices')
        ?.upsert({
          user_id: user?.id,
          session_id: session?.id,
          device_fingerprint: fingerprint,
          device_name: session?.device_name,
          device_type: session?.device_type,
          browser: session?.browser,
          os: session?.os,
          ip_address: session?.ip_address,
          trusted_at: new Date()?.toISOString(),
          last_seen_at: new Date()?.toISOString(),
          is_active: true,
        }, { onConflict: 'user_id,device_fingerprint' });
      if (error) throw error;
      await loadTrustedDevices();
    } catch (err) {
      console.error('handleTrustDevice error:', err);
    } finally {
      setTrustingId(null);
    }
  };

  // Revoke trust for a device
  const handleUntrustDevice = async (session) => {
    if (!user?.id) return;
    setUntrustingId(session?.id);
    try {
      const fingerprint = getDeviceFingerprint(session);
      const { error } = await supabase
        ?.from('trusted_devices')
        ?.update({ is_active: false })
        ?.eq('user_id', user?.id)
        ?.eq('device_fingerprint', fingerprint);
      if (error) throw error;
      await loadTrustedDevices();
    } catch (err) {
      console.error('handleUntrustDevice error:', err);
    } finally {
      setUntrustingId(null);
    }
  };

  // Check if a session's device is trusted
  const isDeviceTrusted = useCallback((session) => {
    const fingerprint = getDeviceFingerprint(session);
    return trustedDevices?.some(td => td?.device_fingerprint === fingerprint && td?.is_active);
  }, [trustedDevices, getDeviceFingerprint]);

  // Load history
  const loadHistory = useCallback(async () => {
    if (!user?.id) { setHistoryLoading(false); return; }
    setHistoryLoading(true);
    try {
      const { data } = await supabase
        ?.from('login_history')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })
        ?.limit(50);

      // Compute suspicious flag client-side based on patterns
      const entries = (data || [])?.map((entry, idx, arr) => {
        let isSuspicious = entry?.is_suspicious || false;
        // Flag failed attempts
        if (entry?.status === 'failed') isSuspicious = true;
        // Flag if same IP had multiple failures in short window
        if (!isSuspicious && idx < arr?.length - 1) {
          const sameIpFailures = arr?.slice(0, idx + 1)?.filter(e =>
            e?.ip_address === entry?.ip_address && e?.status === 'failed' &&
            Math.abs(new Date(e?.created_at) - new Date(entry?.created_at)) < 10 * 60 * 1000
          );
          if (sameIpFailures?.length >= 2) isSuspicious = true;
        }
        return { ...entry, _suspicious: isSuspicious };
      });

      // Detect newly suspicious entries (not yet flagged in DB) and send alerts
      const newlySuspicious = entries?.filter(e => e?._suspicious && !e?.is_suspicious);
      if (newlySuspicious?.length > 0 && user?.email) {
        const mostRecent = newlySuspicious?.[0];
        // Only alert for entries within the last 10 minutes to avoid re-alerting old history
        const isRecent = mostRecent?.created_at &&
          (Date.now() - new Date(mostRecent?.created_at)) < 10 * 60 * 1000;
        if (isRecent) {
          alertSuspiciousLogin({
            userId: user?.id,
            userEmail: user?.email,
            userName: user?.user_metadata?.full_name || user?.email,
            ipAddress: mostRecent?.ip_address,
            deviceName: mostRecent?.device_name,
            browser: mostRecent?.browser,
            failureReason: mostRecent?.failure_reason,
          });
          // Mark as suspicious in DB to avoid duplicate alerts
          await supabase
            ?.from('login_history')
            ?.update({ is_suspicious: true })
            ?.eq('id', mostRecent?.id);
        }
      }

      setHistory(entries);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  }, [user?.id, user?.email, user?.user_metadata]);

  // ── Data Export Helpers ──
  const fetchAllUserData = async () => {
    const [profileRes, alertsRes, wishlistRes, appointmentsRes, historyRes, sessionsRes, prefsRes] = await Promise.allSettled([
      supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single(),
      supabase?.from('property_alerts')?.select('*')?.eq('user_id', user?.id),
      supabase?.from('user_wishlist')?.select('*')?.eq('user_id', user?.id),
      supabase?.from('appointments')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false }),
      supabase?.from('login_history')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false }),
      supabase?.from('login_sessions')?.select('*')?.eq('user_id', user?.id)?.order('last_active_at', { ascending: false }),
      supabase?.from('notification_preferences')?.select('*')?.eq('user_id', user?.id)?.single(),
    ]);

    return {
      exportedAt: new Date()?.toISOString(),
      user: {
        id: user?.id,
        email: user?.email,
        createdAt: user?.created_at,
      },
      profile: profileRes?.status === 'fulfilled' ? (profileRes?.value?.data || {}) : {},
      alerts: alertsRes?.status === 'fulfilled' ? (alertsRes?.value?.data || []) : [],
      wishlist: wishlistRes?.status === 'fulfilled' ? (wishlistRes?.value?.data || []) : [],
      appointments: appointmentsRes?.status === 'fulfilled' ? (appointmentsRes?.value?.data || []) : [],
      activityHistory: historyRes?.status === 'fulfilled' ? (historyRes?.value?.data || []) : [],
      activeSessions: sessionsRes?.status === 'fulfilled' ? (sessionsRes?.value?.data || []) : [],
      notificationPreferences: prefsRes?.status === 'fulfilled' ? (prefsRes?.value?.data || {}) : {},
    };
  };

  const flattenObject = (obj, prefix = '') => {
    return Object.keys(obj || {})?.reduce((acc, key) => {
      const fullKey = prefix ? `${prefix}_${key}` : key;
      const val = obj?.[key];
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        Object.assign(acc, flattenObject(val, fullKey));
      } else if (Array.isArray(val)) {
        acc[fullKey] = JSON.stringify(val);
      } else {
        acc[fullKey] = val;
      }
      return acc;
    }, {});
  };

  const arrayToCSV = (rows) => {
    if (!rows?.length) return '';
    const flat = rows?.map(r => flattenObject(r));
    const headers = [...new Set(flat?.flatMap(r => Object.keys(r)))];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s?.includes(',') || s?.includes('"') || s?.includes('\n') ? `"${s?.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers?.join(','), ...flat?.map(r => headers?.map(h => escape(r?.[h]))?.join(','))];
    return lines?.join('\n');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = filename;
    document?.body?.appendChild(a);
    a?.click();
    document?.body?.removeChild(a);
    URL?.revokeObjectURL(url);
  };

  const handleExportJSON = async () => {
    setExportLoading(true);
    setExportSuccess('');
    try {
      const data = await fetchAllUserData();
      const json = JSON?.stringify(data, null, 2);
      const date = new Date()?.toISOString()?.slice(0, 10);
      downloadFile(json, `kapital-estates-donnees-${date}.json`, 'application/json');
      setExportSuccess('json');
      setTimeout(() => setExportSuccess(''), 4000);
    } catch (err) {
      console.error(err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    setExportSuccess('');
    try {
      const data = await fetchAllUserData();
      const date = new Date()?.toISOString()?.slice(0, 10);
      const sections = [
        { name: 'profil', rows: [{ ...data?.user, ...data?.profile }] },
        { name: 'alertes', rows: data?.alerts },
        { name: 'favoris', rows: data?.wishlist },
        { name: 'rendez-vous', rows: data?.appointments },
        { name: 'historique-connexions', rows: data?.activityHistory },
        { name: 'sessions', rows: data?.activeSessions },
        { name: 'preferences-notifications', rows: [data?.notificationPreferences] },
      ];
      const csvContent = sections
        ?.map(s => `# ${s?.name?.toUpperCase()}\n${arrayToCSV(s?.rows)}`)
        ?.join('\n\n');
      downloadFile(csvContent, `kapital-estates-donnees-${date}.csv`, 'text/csv;charset=utf-8;');
      setExportSuccess('csv');
      setTimeout(() => setExportSuccess(''), 4000);
    } catch (err) {
      console.error(err);
    } finally {
      setExportLoading(false);
    }
  };

  // Register current session on mount
  useEffect(() => {
    if (!user?.id) return;
    const registerSession = async () => {
      const { browser, os, deviceType } = getDeviceInfo();
      const token = `session_${user?.id}_${Date.now()}`;
      // Check if a current session already exists
      const { data: existing } = await supabase
        ?.from('login_sessions')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('is_current', true)
        ?.limit(1);
      if (!existing?.length) {
        await supabase?.from('login_sessions')?.insert({
          user_id: user?.id,
          session_token: token,
          device_name: `${browser} sur ${os}`,
          device_type: deviceType,
          browser,
          os,
          is_current: true,
          last_active_at: new Date()?.toISOString(),
        });
        // Also log to history
        await supabase?.from('login_history')?.insert({
          user_id: user?.id,
          device_name: `${browser} sur ${os}`,
          device_type: deviceType,
          browser,
          os,
          status: 'success',
          user_agent: navigator?.userAgent || '',
          is_suspicious: false,
        });
      }
    };
    registerSession();
    loadSessions();
    loadHistory();
    load2FASettings();
    loadTrustedDevices();
  }, [user?.id, getDeviceInfo, loadSessions, loadHistory, load2FASettings, loadTrustedDevices]);

  // Change password
  const handleChangePassword = async (e) => {
    e?.preventDefault();
    setPwError('');
    if (!pwForm?.next?.trim()) { setPwError('Veuillez saisir un nouveau mot de passe.'); return; }
    if (pwForm?.next?.length < 8) { setPwError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (pwForm?.next !== pwForm?.confirm) { setPwError('Les mots de passe ne correspondent pas.'); return; }
    setPwLoading(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: pwForm?.next });
      if (error) { setPwError(error?.message || 'Erreur lors du changement de mot de passe.'); return; }
      setPwSuccess(true);
      setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwSuccess(false), 4000);
    } catch {
      setPwError('Une erreur inattendue est survenue.');
    } finally {
      setPwLoading(false);
    }
  };

  // Revoke session
  const handleRevokeSession = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      const revokedSession = sessions?.find(s => s?.id === sessionId);
      await supabase?.from('login_sessions')?.delete()?.eq('id', sessionId);
      setSessions(prev => prev?.filter(s => s?.id !== sessionId));
      setSessionExpanded(null);
      // Send session terminated alert (revoked)
      if (user?.id && user?.email) {
        await alertSessionTerminated({
          userId: user?.id,
          userEmail: user?.email,
          userName: user?.user_metadata?.full_name || user?.email,
          deviceName: revokedSession?.device_name,
          reason: 'revoked',
        });
      }
    } finally {
      setRevokingId(null);
    }
  };

  // Revoke all other sessions
  const handleRevokeAllSessions = async () => {
    setRevokingAll(true);
    try {
      const otherSessions = sessions?.filter(s => !s?.is_current);
      await supabase
        ?.from('login_sessions')
        ?.delete()
        ?.eq('user_id', user?.id)
        ?.eq('is_current', false);
      setSessions(prev => prev?.filter(s => s?.is_current));
      setSessionExpanded(null);
      setRevokeAllConfirm(false);
      if (user?.id && user?.email && otherSessions?.length > 0) {
        await alertSessionTerminated({
          userId: user?.id,
          userEmail: user?.email,
          userName: user?.user_metadata?.full_name || user?.email,
          deviceName: `${otherSessions?.length} session(s)`,
          reason: 'revoked_all',
        });
      }
    } finally {
      setRevokingAll(false);
    }
  };

  const deviceIcon = (type) => {
    if (type === 'mobile') return 'Smartphone';
    if (type === 'tablet') return 'Tablet';
    return 'Monitor';
  };

  return (
    <div className="space-y-8">
      {/* ── Session Timeout Enforcement ── */}
      <section>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isWarning ? 'bg-red-50' : 'bg-blue-50'}`}>
            <Icon name="Timer" size={14} className={isWarning ? 'text-red-600' : 'text-blue-600'} />
          </div>
          Expiration de session
        </h3>

        {sessionExpired ? (
          <div className="rounded-2xl p-5 border bg-red-50/60 border-red-200">
            {recoveryStep === 'success' ? (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldCheck" size={22} className="text-green-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-semibold text-green-700 mb-1">Identité vérifiée</p>
                  <p className="text-ios-caption1 text-green-600 leading-relaxed">
                    Votre identité a été confirmée. Votre session a été restaurée avec succès.
                  </p>
                </div>
              </div>
            ) : recoveryStep === 'verify_email' || recoveryStep === 'verify_sms' ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon name={recoveryStep === 'verify_email' ? 'Mail' : 'MessageSquare'} size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-text-primary mb-0.5">Vérification de récupération</p>
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      Un code de récupération a été envoyé{' '}
                      {recoveryStep === 'verify_email'
                        ? `à ${twoFASettings?.recovery_email || user?.email}`
                        : `par SMS au ${twoFASettings?.recovery_phone}`
                      }. Saisissez-le ci-dessous pour restaurer votre session.
                    </p>
                    <p className="text-[10px] text-blue-600 mt-1 font-mono bg-blue-50 px-2 py-1 rounded-lg inline-block">
                      Code de démo : {recoverySentCode}
                    </p>
                  </div>
                </div>
                <div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={recoveryCode}
                    onChange={e => { setRecoveryCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6)); setRecoveryCodeError(''); }}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-border rounded-xl text-ios-title3 font-mono tracking-[0.3em] text-center focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  />
                  {recoveryCodeError && (
                    <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex items-center gap-2 mt-2">
                      <Icon name="AlertCircle" size={13} />
                      {recoveryCodeError}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleVerifyRecoveryCode}
                    disabled={recoveryCode?.length !== 6 || recoveryActionLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-blue-700 disabled:opacity-50 transition-all shadow-ios-light"
                  >
                    {recoveryActionLoading
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Icon name="ShieldCheck" size={15} color="white" />
                    }
                    <span>Vérifier et restaurer</span>
                  </button>
                  <button
                    onClick={() => setRecoveryStep('choose')}
                    className="px-4 py-2 bg-white text-text-secondary rounded-xl text-ios-caption1 font-body-medium border border-border/50 hover:bg-surface transition-all"
                  >
                    Retour
                  </button>
                </div>
              </div>
            ) : recoveryStep === 'choose' ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="KeyRound" size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-text-primary mb-0.5">Options de récupération</p>
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      Choisissez comment vérifier votre identité pour restaurer votre session.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(twoFASettings?.recovery_email || user?.email) && (
                    <button
                      onClick={() => handleStartSessionRecovery('email')}
                      className="flex items-start gap-3 p-4 rounded-2xl border-2 border-blue-200 bg-blue-50/40 text-left hover:border-blue-400 transition-all"
                    >
                      <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="Mail" size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-ios-callout font-body-semibold text-blue-700 mb-0.5">Email de récupération</p>
                        <p className="text-[10px] text-text-secondary">{twoFASettings?.recovery_email || user?.email}</p>
                      </div>
                    </button>
                  )}
                  {twoFASettings?.recovery_phone && (
                    <button
                      onClick={() => handleStartSessionRecovery('sms')}
                      className="flex items-start gap-3 p-4 rounded-2xl border-2 border-blue-200 bg-blue-50/40 text-left hover:border-blue-400 transition-all"
                    >
                      <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="MessageSquare" size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-ios-callout font-body-semibold text-blue-700 mb-0.5">SMS de récupération</p>
                        <p className="text-[10px] text-text-secondary">{twoFASettings?.recovery_phone}</p>
                      </div>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setRecoveryStep('idle')}
                  className="text-ios-caption1 text-text-secondary hover:text-text-primary transition-colors underline"
                >
                  Annuler — me déconnecter
                </button>
              </div>
            ) : (
              /* Default expired state */
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldOff" size={22} className="text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-ios-callout font-body-semibold text-red-700 mb-1">Session expirée</p>
                  <p className="text-ios-caption1 text-red-600 leading-relaxed mb-4">
                    Votre session a expiré après 30 minutes d'inactivité. Vous avez été déconnecté automatiquement pour protéger votre compte.
                  </p>
                  {/* Recovery options if configured */}
                  {(twoFASettings?.recovery_email || twoFASettings?.recovery_phone || user?.email) && (
                    <div className="space-y-3">
                      <p className="text-ios-caption1 font-body-semibold text-red-700 flex items-center gap-1.5">
                        <Icon name="KeyRound" size={13} className="text-red-600" />
                        Options de récupération de session
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setRecoveryStep('choose')}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-blue-700 transition-all shadow-ios-light"
                        >
                          <Icon name="KeyRound" size={13} color="white" />
                          <span>Récupérer ma session</span>
                        </button>
                        <button
                          onClick={handleAutoLogout}
                          className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 border border-red-200 rounded-xl text-ios-caption1 font-body-medium hover:bg-red-50 transition-all"
                        >
                          <Icon name="LogOut" size={13} className="text-red-600" />
                          <span>Se déconnecter</span>
                        </button>
                      </div>
                      <p className="text-[10px] text-red-600/70 flex items-start gap-1.5">
                        <Icon name="Info" size={10} className="mt-0.5 flex-shrink-0" />
                        La récupération envoie un code de vérification à votre email ou téléphone de récupération configuré.
                      </p>
                    </div>
                  )}
                  {!twoFASettings?.recovery_email && !twoFASettings?.recovery_phone && !user?.email && (
                    <button
                      onClick={handleAutoLogout}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-red-700 transition-all shadow-ios-light"
                    >
                      <Icon name="LogOut" size={13} color="white" />
                      <span>Se reconnecter</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={`rounded-2xl p-5 border transition-all ${isWarning ? 'bg-red-50/50 border-red-200' : 'bg-blue-50/30 border-blue-100'}`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isWarning ? 'bg-red-100' : 'bg-blue-100'}`}>
                <Icon name={isWarning ? 'AlertTriangle' : 'ShieldCheck'} size={22} className={isWarning ? 'text-red-600' : 'text-blue-600'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-ios-callout font-body-semibold text-text-primary">
                    {isWarning ? 'Session bientôt expirée' : 'Session active'}
                  </p>
                  <span className={`text-[10px] font-body-semibold px-2.5 py-0.5 rounded-full ${isWarning ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {isWarning ? '⚠ Attention' : '● Actif'}
                  </span>
                </div>
                <p className="text-ios-caption1 text-text-secondary mb-3 leading-relaxed">
                  {isWarning
                    ? 'Votre session va expirer dans moins de 5 minutes. Cliquez sur "Prolonger" pour rester connecté.' :'Votre session expire automatiquement après 30 minutes d\'inactivité pour protéger votre compte.'}
                </p>

                {/* Countdown display */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-ios-caption1 text-text-secondary flex items-center gap-1">
                      <Icon name="Clock" size={11} />
                      Temps d'inactivité restant
                    </span>
                    <span className={`text-ios-callout font-body-semibold tabular-nums ${isWarning ? 'text-red-600' : 'text-blue-700'}`}>
                      {formatCountdown(idleRemaining)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 w-full bg-border/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        progressPct > 50 ? 'bg-blue-500' :
                        progressPct > 16 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-text-secondary">0:00</span>
                    <span className="text-[10px] text-text-secondary">30:00</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExtendSession}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-ios-caption1 font-body-medium transition-all shadow-ios-light ${
                      isWarning
                        ? 'bg-red-600 text-white hover:bg-red-700' :'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Icon name="RefreshCw" size={13} color="white" />
                    <span>Prolonger la session</span>
                  </button>
                  <button
                    onClick={handleAutoLogout}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-ios-caption1 font-body-medium bg-white text-red-600 border border-red-200 hover:bg-red-50 transition-all"
                  >
                    <Icon name="LogOut" size={13} className="text-red-600" />
                    <span>Se déconnecter maintenant</span>
                  </button>
                </div>
              </div>
            </div>

            {isWarning && (
              <div className="mt-4 pt-4 border-t border-red-200/60">
                <p className="text-[10px] text-red-700/80 flex items-start gap-1.5">
                  <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0" />
                  Toute activité sur la page (déplacement de souris, frappe au clavier, défilement) remet le compteur à zéro automatiquement.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info note */}
        {!sessionExpired && (
          <p className="mt-3 text-[10px] text-text-secondary flex items-start gap-1.5">
            <Icon name="ShieldCheck" size={11} className="mt-0.5 flex-shrink-0 text-blue-500" />
            La déconnexion automatique après 30 minutes d'inactivité protège votre compte contre les accès non autorisés sur des appareils partagés.
          </p>
        )}
      </section>

      {/* ── Email Verification Status ── */}
      <section>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isEmailVerified ? 'bg-green-50' : 'bg-amber-50'}`}>
            <Icon name={isEmailVerified ? 'MailCheck' : 'MailWarning'} size={14} className={isEmailVerified ? 'text-green-600' : 'text-amber-600'} />
          </div>
          Vérification de l'adresse email
        </h3>
        <div className={`rounded-2xl p-5 border ${isEmailVerified ? 'bg-green-50/40 border-green-200' : 'bg-amber-50/40 border-amber-200'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isEmailVerified ? 'bg-green-100' : 'bg-amber-100'}`}>
              <Icon name={isEmailVerified ? 'ShieldCheck' : 'ShieldAlert'} size={22} className={isEmailVerified ? 'text-green-600' : 'text-amber-600'} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-ios-callout font-body-semibold text-text-primary">
                  {isEmailVerified ? 'Email vérifié' : 'Email non vérifié'}
                </p>
                <span className={`text-[10px] font-body-semibold px-2.5 py-0.5 rounded-full ${isEmailVerified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {isEmailVerified ? '✓ Confirmé' : '⚠ En attente'}
                </span>
              </div>
              <p className="text-ios-caption1 text-text-secondary mb-1">
                <span className="font-body-medium text-text-primary">{user?.email}</span>
              </p>
              {isEmailVerified ? (
                <p className="text-ios-caption1 text-green-700">
                  Vérifié le {formatDate(user?.email_confirmed_at)}. Votre compte est pleinement activé.
                </p>
              ) : (
                <p className="text-ios-caption1 text-amber-700 leading-relaxed">
                  Votre compte n'est pas encore activé. Vérifiez votre boîte mail et cliquez sur le lien de confirmation pour activer votre compte.
                </p>
              )}
            </div>
          </div>

          {!isEmailVerified && (
            <div className="mt-4 pt-4 border-t border-amber-200/60 space-y-3">
              <div className="flex flex-wrap gap-3 items-center">
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendCooldown > 0}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-amber-700 disabled:opacity-60 transition-all shadow-ios-light"
                >
                  {resendLoading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Icon name="Send" size={15} color="white" />
                  }
                  <span>
                    {resendCooldown > 0
                      ? `Renvoyer dans ${resendCooldown}s`
                      : 'Renvoyer la vérification'
                    }
                  </span>
                </button>
                {resendCooldown > 0 && (
                  <p className="text-ios-caption1 text-text-secondary">
                    Un email a été envoyé à <span className="font-body-medium">{user?.email}</span>
                  </p>
                )}
              </div>
              {resendSuccess && (
                <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
                  <Icon name="CheckCircle" size={13} />
                  Email de vérification envoyé ! Vérifiez votre boîte de réception et vos spams.
                </p>
              )}
              {resendError && (
                <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex items-center gap-2">
                  <Icon name="AlertCircle" size={13} />
                  {resendError}
                </p>
              )}
              <p className="text-[10px] text-amber-700/80 flex items-start gap-1.5">
                <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0" />
                Si vous ne recevez pas l'email, vérifiez vos spams ou attendez quelques minutes avant de renvoyer.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Double Authentification (2FA) ── */}
      <section>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${is2FAEnabled ? 'bg-emerald-50' : 'bg-violet-50'}`}>
            <Icon name="ShieldPlus" size={14} className={is2FAEnabled ? 'text-emerald-600' : 'text-violet-600'} />
          </div>
          Double authentification (2FA)
        </h3>

        {twoFALoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* ── Status Banner ── */}
            <div className={`rounded-2xl p-5 border transition-all ${is2FAEnabled ? 'bg-emerald-50/40 border-emerald-200' : 'bg-violet-50/30 border-violet-100'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${is2FAEnabled ? 'bg-emerald-100' : 'bg-violet-100'}`}>
                  <Icon name={is2FAEnabled ? 'ShieldCheck' : 'ShieldOff'} size={22} className={is2FAEnabled ? 'text-emerald-600' : 'text-violet-500'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-ios-callout font-body-semibold text-text-primary">
                      {is2FAEnabled ? '2FA activée' : '2FA désactivée'}
                    </p>
                    <span className={`text-[10px] font-body-semibold px-2.5 py-0.5 rounded-full ${is2FAEnabled ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-violet-100 text-violet-700 border border-violet-200'}`}>
                      {is2FAEnabled ? '● Protégé' : '○ Non configuré'}
                    </span>
                    {is2FAEnabled && twoFASettings?.method && (
                      <span className="text-[10px] font-body-medium px-2.5 py-0.5 rounded-full bg-surface text-text-secondary border border-border/40 flex items-center gap-1">
                        <Icon name={twoFASettings?.method === 'sms' ? 'MessageSquare' : 'Mail'} size={9} />
                        {twoFASettings?.method === 'sms' ? 'SMS' : 'Email'}
                      </span>
                    )}
                  </div>
                  {is2FAEnabled ? (
                    <div className="space-y-1">
                      <p className="text-ios-caption1 text-emerald-700 leading-relaxed">
                        Votre compte est protégé par une vérification en deux étapes via{' '}
                        <span className="font-body-semibold">{twoFASettings?.method === 'sms' ? `SMS (${twoFASettings?.phone_number || 'numéro enregistré'})` : `email (${user?.email})`}</span>.
                      </p>
                      {twoFASettings?.enabled_at && (
                        <p className="text-[10px] text-emerald-600/80">
                          Activée le {formatDate(twoFASettings?.enabled_at, true)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      Ajoutez une couche de sécurité supplémentaire. À chaque connexion, un code de vérification vous sera demandé en plus de votre mot de passe.
                    </p>
                  )}
                </div>
              </div>

              {/* ── Active 2FA: Quick Actions ── */}
              {is2FAEnabled && twoFAStep !== 'disable_confirm' && (
                <div className="mt-4 pt-4 border-t border-emerald-200/60 flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowBackupCodes(v => !v)}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-xl text-ios-caption1 font-body-medium border border-emerald-200 hover:bg-emerald-50 transition-all"
                  >
                    <Icon name="Key" size={13} className="text-emerald-600" />
                    <span>{showBackupCodes ? 'Masquer' : 'Codes de secours'}</span>
                  </button>
                  <button
                    onClick={() => setTwoFAStep('disable_confirm')}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 rounded-xl text-ios-caption1 font-body-medium border border-red-200 hover:bg-red-50 transition-all"
                  >
                    <Icon name="ShieldOff" size={13} className="text-red-600" />
                    <span>Désactiver la 2FA</span>
                  </button>
                </div>
              )}

              {/* ── Inactive 2FA: Setup CTA ── */}
              {!is2FAEnabled && twoFAStep === 'idle' && (
                <div className="mt-4 pt-4 border-t border-violet-100/60">
                  <button
                    onClick={() => setTwoFAStep('choose_method')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-violet-700 transition-all shadow-ios-light"
                  >
                    <Icon name="ShieldPlus" size={15} color="white" />
                    <span>Configurer la 2FA</span>
                  </button>
                </div>
              )}
            </div>

            {/* ── Success message ── */}
            {twoFASuccess && (
              <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
                <Icon name="CheckCircle" size={13} />
                {twoFASuccess}
              </p>
            )}

            {/* ── Backup Codes Panel ── */}
            {is2FAEnabled && showBackupCodes && twoFASettings?.backup_codes?.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Icon name="Key" size={14} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-text-primary">Codes de récupération de secours</p>
                    <p className="text-[10px] text-text-secondary">Utilisez ces codes si vous perdez accès à votre méthode 2FA</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {twoFASettings?.backup_codes?.map((code, idx) => {
                    const isUsed = twoFASettings?.backup_codes_used?.includes(idx);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border font-mono text-ios-caption1 ${
                          isUsed
                            ? 'bg-surface/60 border-border/30 text-text-secondary line-through' :'bg-white border-amber-200 text-text-primary'
                        }`}
                      >
                        <Icon name={isUsed ? 'CheckCircle' : 'Hash'} size={11} className={isUsed ? 'text-text-secondary' : 'text-amber-500'} />
                        <span>{code}</span>
                        {isUsed && <span className="text-[9px] text-text-secondary ml-auto">Utilisé</span>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-amber-700/80 flex items-start gap-1.5 pt-1">
                  <Icon name="AlertTriangle" size={11} className="mt-0.5 flex-shrink-0" />
                  Conservez ces codes dans un endroit sûr. Chaque code ne peut être utilisé qu'une seule fois.
                </p>
              </div>
            )}

            {/* ── Disable Confirm Panel ── */}
            {twoFAStep === 'disable_confirm' && (
              <div className="rounded-2xl border border-red-200 bg-red-50/40 p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="ShieldOff" size={18} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-red-700 mb-1">Désactiver la double authentification ?</p>
                    <p className="text-ios-caption1 text-red-600/80 leading-relaxed">
                      Votre compte sera moins protégé. Vous pourrez réactiver la 2FA à tout moment depuis cet onglet.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleDisable2FA}
                    disabled={twoFAActionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-red-700 disabled:opacity-60 transition-all shadow-ios-light"
                  >
                    {twoFAActionLoading
                      ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Icon name="ShieldOff" size={13} color="white" />
                    }
                    <span>Confirmer la désactivation</span>
                  </button>
                  <button
                    onClick={() => setTwoFAStep('idle')}
                    className="px-4 py-2 bg-white text-text-secondary rounded-xl text-ios-caption1 font-body-medium border border-border/50 hover:bg-surface transition-all"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* ── Setup Flow: Step 1 — Choose Method ── */}
            {twoFAStep === 'choose_method' && (
              <div className="rounded-2xl border border-violet-200 bg-violet-50/30 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-ios-callout font-body-semibold text-text-primary">Choisir la méthode de vérification</p>
                  <button onClick={resetTwoFAFlow} className="w-7 h-7 rounded-lg bg-surface border border-border/40 flex items-center justify-center hover:bg-border/20 transition-all">
                    <Icon name="X" size={13} className="text-text-secondary" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Email option */}
                  <button
                    onClick={() => setTwoFAMethod('email')}
                    className={`flex items-start gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                      twoFAMethod === 'email' ?'border-violet-500 bg-violet-50' :'border-border/50 bg-white hover:border-violet-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${twoFAMethod === 'email' ? 'bg-violet-100' : 'bg-surface'}`}>
                      <Icon name="Mail" size={18} className={twoFAMethod === 'email' ? 'text-violet-600' : 'text-text-secondary'} />
                    </div>
                    <div>
                      <p className={`text-ios-callout font-body-semibold mb-0.5 ${twoFAMethod === 'email' ? 'text-violet-700' : 'text-text-primary'}`}>
                        Email
                      </p>
                      <p className="text-[10px] text-text-secondary leading-relaxed">
                        Code envoyé à<br />
                        <span className="font-body-medium text-text-primary">{user?.email}</span>
                      </p>
                    </div>
                    {twoFAMethod === 'email' && (
                      <div className="ml-auto flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center">
                          <Icon name="Check" size={11} color="white" />
                        </div>
                      </div>
                    )}
                  </button>

                  {/* SMS option */}
                  <button
                    onClick={() => setTwoFAMethod('sms')}
                    className={`flex items-start gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                      twoFAMethod === 'sms' ?'border-violet-500 bg-violet-50' :'border-border/50 bg-white hover:border-violet-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${twoFAMethod === 'sms' ? 'bg-violet-100' : 'bg-surface'}`}>
                      <Icon name="MessageSquare" size={18} className={twoFAMethod === 'sms' ? 'text-violet-600' : 'text-text-secondary'} />
                    </div>
                    <div>
                      <p className={`text-ios-callout font-body-semibold mb-0.5 ${twoFAMethod === 'sms' ? 'text-violet-700' : 'text-text-primary'}`}>
                        SMS
                      </p>
                      <p className="text-[10px] text-text-secondary leading-relaxed">
                        Code envoyé par SMS<br />sur votre téléphone
                      </p>
                    </div>
                    {twoFAMethod === 'sms' && (
                      <div className="ml-auto flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center">
                          <Icon name="Check" size={11} color="white" />
                        </div>
                      </div>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => {
                    if (twoFAMethod === 'sms') {
                      setTwoFAStep('enter_phone');
                    } else {
                      setTwoFAStep('verify_code');
                      handleSend2FACode();
                    }
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-violet-700 transition-all shadow-ios-light"
                >
                  <Icon name="ArrowRight" size={15} color="white" />
                  <span>Continuer avec {twoFAMethod === 'sms' ? 'SMS' : 'Email'}</span>
                </button>
              </div>
            )}

            {/* ── Setup Flow: Step 1b — Enter Phone (SMS only) ── */}
            {twoFAStep === 'enter_phone' && (
              <div className="rounded-2xl border border-violet-200 bg-violet-50/30 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-ios-callout font-body-semibold text-text-primary">Entrez votre numéro de téléphone</p>
                  <button onClick={resetTwoFAFlow} className="w-7 h-7 rounded-lg bg-surface border border-border/40 flex items-center justify-center hover:bg-border/20 transition-all">
                    <Icon name="X" size={13} className="text-text-secondary" />
                  </button>
                </div>
                <div>
                  <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">
                    Numéro de téléphone (avec indicatif pays)
                  </label>
                  <input
                    type="tel"
                    value={twoFAPhone}
                    onChange={e => setTwoFAPhone(e?.target?.value)}
                    placeholder="+225 07 00 00 00 00"
                    className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                  />
                  <p className="text-[10px] text-text-secondary mt-1.5">
                    Exemple : +225 07 12 34 56 78 (Côte d'Ivoire)
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      if (!twoFAPhone?.trim()) return;
                      setTwoFAStep('verify_code');
                      handleSend2FACode();
                    }}
                    disabled={!twoFAPhone?.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-violet-700 disabled:opacity-50 transition-all shadow-ios-light"
                  >
                    <Icon name="Send" size={15} color="white" />
                    <span>Envoyer le code SMS</span>
                  </button>
                  <button
                    onClick={() => setTwoFAStep('choose_method')}
                    className="px-4 py-2 bg-white text-text-secondary rounded-xl text-ios-caption1 font-body-medium border border-border/50 hover:bg-surface transition-all"
                  >
                    Retour
                  </button>
                </div>
              </div>
            )}

            {/* ── Setup Flow: Step 2 — Verify Code ── */}
            {twoFAStep === 'verify_code' && (
              <div className="rounded-2xl border border-violet-200 bg-violet-50/30 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-ios-callout font-body-semibold text-text-primary">Vérification du code</p>
                  <button onClick={resetTwoFAFlow} className="w-7 h-7 rounded-lg bg-surface border border-border/40 flex items-center justify-center hover:bg-border/20 transition-all">
                    <Icon name="X" size={13} className="text-text-secondary" />
                  </button>
                </div>

                {twoFACodeSent ? (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-violet-100/60 border border-violet-200">
                    <Icon name={twoFAMethod === 'sms' ? 'MessageSquare' : 'Mail'} size={15} className="text-violet-600 mt-0.5 flex-shrink-0" />
                    <p className="text-ios-caption1 text-violet-700 leading-relaxed">
                      Un code à 6 chiffres a été envoyé{' '}
                      {twoFAMethod === 'sms'
                        ? `par SMS au ${twoFAPhone}`
                        : `à ${user?.email}`
                      }. Saisissez-le ci-dessous.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2 text-ios-caption1 text-text-secondary">Envoi du code en cours…</span>
                  </div>
                )}

                {twoFACodeSent && (
                  <>
                    <div>
                      <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">
                        Code de vérification à 6 chiffres
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={twoFACode}
                        onChange={e => {
                          setTwoFACode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                          setTwoFACodeError('');
                        }}
                        placeholder="000000"
                        className="w-full px-4 py-3 border border-border rounded-xl text-ios-title3 font-mono tracking-[0.3em] text-center focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                      />
                    </div>

                    {twoFACodeError && (
                      <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex items-center gap-2">
                        <Icon name="AlertCircle" size={13} />
                        {twoFACodeError}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 items-center">
                      <button
                        onClick={handleVerify2FACode}
                        disabled={twoFACode?.length !== 6 || twoFAActionLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-violet-700 disabled:opacity-50 transition-all shadow-ios-light"
                      >
                        {twoFAActionLoading
                          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Icon name="ShieldCheck" size={15} color="white" />
                        }
                        <span>Vérifier et activer</span>
                      </button>
                      <button
                        onClick={handleSend2FACode}
                        disabled={twoFACooldown > 0 || twoFAActionLoading}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white text-violet-600 rounded-xl text-ios-caption1 font-body-medium border border-violet-200 hover:bg-violet-50 disabled:opacity-50 transition-all"
                      >
                        <Icon name="RefreshCw" size={12} className="text-violet-600" />
                        <span>{twoFACooldown > 0 ? `Renvoyer (${twoFACooldown}s)` : 'Renvoyer le code'}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Setup Flow: Step 3 — Show Backup Codes ── */}
            {twoFAStep === 'show_backup' && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="ShieldCheck" size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-emerald-700 mb-0.5">2FA activée avec succès !</p>
                    <p className="text-ios-caption1 text-emerald-600/80 leading-relaxed">
                      Votre compte est maintenant protégé. Sauvegardez vos codes de récupération ci-dessous.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-ios-caption1 font-body-semibold text-text-primary mb-2 flex items-center gap-1.5">
                    <Icon name="Key" size={13} className="text-amber-500" />
                    Codes de récupération de secours
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {twoFABackupCodes?.map((code, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 bg-white font-mono text-ios-caption1 text-text-primary">
                        <Icon name="Hash" size={11} className="text-amber-500 flex-shrink-0" />
                        <span>{code}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-amber-700/80 mt-2 flex items-start gap-1.5">
                    <Icon name="AlertTriangle" size={11} className="mt-0.5 flex-shrink-0" />
                    Notez ces codes et conservez-les en lieu sûr. Ils vous permettront d'accéder à votre compte si vous perdez accès à votre méthode 2FA.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setTwoFAStep('idle');
                    setTwoFASuccess('La double authentification a été activée avec succès.');
                    setTimeout(() => setTwoFASuccess(''), 5000);
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-emerald-700 transition-all shadow-ios-light"
                >
                  <Icon name="Check" size={15} color="white" />
                  <span>J'ai sauvegardé mes codes</span>
                </button>
              </div>
            )}

            {/* Info note */}
            {twoFAStep === 'idle' && (
              <p className="text-[10px] text-text-secondary flex items-start gap-1.5">
                <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0 text-violet-400" />
                La double authentification ajoute une couche de protection supplémentaire en exigeant un code unique à chaque connexion, même si votre mot de passe est compromis.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ── MFA Enforcement & Enrollment Avancé ── */}
      {is2FAEnabled && (
        <section>
          <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Icon name="ShieldEllipsis" size={14} className="text-indigo-600" />
            </div>
            Enrollment 2FA avancé
          </h3>

          <div className="space-y-4">

            {/* ── MFA Enforcement Toggle ── */}
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/30 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${mfaEnforced ? 'bg-indigo-100' : 'bg-surface'}`}>
                    <Icon name={mfaEnforced ? 'ShieldCheck' : 'Shield'} size={18} className={mfaEnforced ? 'text-indigo-600' : 'text-text-secondary'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-ios-callout font-body-semibold text-text-primary">Enforcement MFA</p>
                      <span className={`text-[10px] font-body-semibold px-2.5 py-0.5 rounded-full border ${mfaEnforced ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-surface text-text-secondary border-border/40'}`}>
                        {mfaEnforced ? '● Activé' : '○ Désactivé'}
                      </span>
                    </div>
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      {mfaEnforced
                        ? 'Le défi 2FA est exigé à chaque connexion, même sur les appareils de confiance.' :'Les appareils de confiance peuvent ignorer le défi 2FA. Activez pour forcer la vérification à chaque connexion.'}
                    </p>
                  </div>
                </div>

                {/* Toggle switch */}
                <button
                  onClick={handleToggleMFAEnforcement}
                  disabled={mfaEnforcedLoading}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none ${
                    mfaEnforced ? 'bg-indigo-600' : 'bg-border'
                  } ${mfaEnforcedLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  role="switch"
                  aria-checked={mfaEnforced}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${mfaEnforced ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {mfaEnforcedSuccess && (
                <p className="mt-3 text-ios-caption1 text-indigo-700 bg-indigo-100/60 px-3 py-2 rounded-xl border border-indigo-200 flex items-center gap-2">
                  <Icon name="CheckCircle" size={13} />
                  {mfaEnforcedSuccess}
                </p>
              )}

              {mfaEnforced && (
                <div className="mt-3 pt-3 border-t border-indigo-100/60">
                  <p className="text-[10px] text-indigo-700/80 flex items-start gap-1.5">
                    <Icon name="AlertTriangle" size={11} className="mt-0.5 flex-shrink-0" />
                    L'enforcement MFA remplace le comportement des appareils de confiance. Chaque connexion exigera un code de vérification.
                  </p>
                </div>
              )}
            </div>

            {/* ── TOTP Backup Codes Management ── */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50/30 p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="KeyRound" size={18} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-text-primary mb-0.5">Codes de secours TOTP</p>
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      {twoFASettings?.backup_codes?.length || 0} codes disponibles
                      {twoFASettings?.backup_codes_used?.length > 0 && ` · ${twoFASettings?.backup_codes_used?.length} utilisé(s)`}
                      {twoFASettings?.backup_codes_regenerated_at && (
                        <span className="block text-[10px] text-text-secondary mt-0.5">
                          Régénérés le {new Date(twoFASettings?.backup_codes_regenerated_at)?.toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowBackupCodes(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white text-amber-700 rounded-xl text-ios-caption1 font-body-medium border border-amber-200 hover:bg-amber-50 transition-all"
                  >
                    <Icon name={showBackupCodes ? 'EyeOff' : 'Eye'} size={13} className="text-amber-600" />
                    <span className="hidden sm:inline">{showBackupCodes ? 'Masquer' : 'Afficher'}</span>
                  </button>
                </div>
              </div>

              {/* Existing backup codes display */}
              {showBackupCodes && twoFASettings?.backup_codes?.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {twoFASettings?.backup_codes?.map((code, idx) => {
                      const isUsed = twoFASettings?.backup_codes_used?.includes(idx);
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border font-mono text-ios-caption1 ${
                            isUsed
                              ? 'bg-surface/60 border-border/30 text-text-secondary line-through' :'bg-white border-amber-200 text-text-primary'
                          }`}
                        >
                          <Icon name={isUsed ? 'CheckCircle' : 'Hash'} size={11} className={isUsed ? 'text-text-secondary' : 'text-amber-500'} />
                          <span>{code}</span>
                          {isUsed && <span className="text-[9px] text-text-secondary ml-auto">Utilisé</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New backup codes after regeneration */}
              {showNewBackupCodes && newBackupCodes?.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
                  <p className="text-ios-caption1 font-body-semibold text-emerald-700 flex items-center gap-1.5">
                    <Icon name="CheckCircle" size={13} />
                    Nouveaux codes générés — sauvegardez-les maintenant
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {newBackupCodes?.map((code, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-200 bg-white font-mono text-ios-caption1 text-text-primary">
                        <Icon name="Hash" size={11} className="text-emerald-500 flex-shrink-0" />
                        <span>{code}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowNewBackupCodes(false)}
                    className="text-[10px] text-emerald-700 font-body-medium underline"
                  >
                    J'ai sauvegardé ces codes
                  </button>
                </div>
              )}

              {/* Regenerate confirm */}
              {regenConfirm ? (
                <div className="rounded-xl border border-red-200 bg-red-50/40 p-4 space-y-3">
                  <p className="text-ios-caption1 font-body-semibold text-red-700 flex items-center gap-1.5">
                    <Icon name="AlertTriangle" size={13} />
                    Régénérer les codes de secours ?
                  </p>
                  <p className="text-[10px] text-red-600/80 leading-relaxed">
                    Les anciens codes seront immédiatement invalidés. Assurez-vous de sauvegarder les nouveaux codes dès leur génération.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleRegenerateBackupCodes}
                      disabled={regenLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-red-700 disabled:opacity-60 transition-all shadow-ios-light"
                    >
                      {regenLoading
                        ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <Icon name="RefreshCw" size={13} color="white" />
                      }
                      <span>Confirmer la régénération</span>
                    </button>
                    <button
                      onClick={() => setRegenConfirm(false)}
                      className="px-4 py-2 bg-white text-text-secondary rounded-xl text-ios-caption1 font-body-medium border border-border/50 hover:bg-surface transition-all"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setRegenConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-amber-700 rounded-xl text-ios-caption1 font-body-medium border border-amber-200 hover:bg-amber-50 transition-all"
                >
                  <Icon name="RefreshCw" size={13} className="text-amber-600" />
                  <span>Régénérer les codes de secours</span>
                </button>
              )}

              {regenSuccess && (
                <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
                  <Icon name="CheckCircle" size={13} />
                  {regenSuccess}
                </p>
              )}

              <p className="text-[10px] text-amber-700/80 flex items-start gap-1.5">
                <Icon name="AlertTriangle" size={11} className="mt-0.5 flex-shrink-0" />
                Chaque code ne peut être utilisé qu'une seule fois. Conservez-les dans un gestionnaire de mots de passe ou un endroit sûr hors ligne.
              </p>
            </div>

            {/* ── Recovery Options ── */}
            <div className="rounded-2xl border border-teal-200 bg-teal-50/30 p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="LifeBuoy" size={18} className="text-teal-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-semibold text-text-primary mb-0.5">Options de récupération</p>
                    <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                      Configurez un email et/ou numéro de téléphone de secours pour récupérer l'accès à votre compte après un timeout de session ou perte d'accès à votre méthode 2FA.
                    </p>
                    {(twoFASettings?.recovery_email || twoFASettings?.recovery_phone) && !showRecoveryForm && (
                      <div className="mt-2 space-y-1">
                        {twoFASettings?.recovery_email && (
                          <p className="text-[10px] text-teal-700 flex items-center gap-1.5">
                            <Icon name="Mail" size={10} />
                            Email : <span className="font-body-medium">{twoFASettings?.recovery_email}</span>
                          </p>
                        )}
                        {twoFASettings?.recovery_phone && (
                          <p className="text-[10px] text-teal-700 flex items-center gap-1.5">
                            <Icon name="Phone" size={10} />
                            Téléphone : <span className="font-body-medium">{twoFASettings?.recovery_phone}</span>
                          </p>
                        )}
                        {twoFASettings?.recovery_options_set_at && (
                          <p className="text-[10px] text-text-secondary">
                            Configuré le {new Date(twoFASettings?.recovery_options_set_at)?.toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowRecoveryForm(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white text-teal-700 rounded-xl text-ios-caption1 font-body-medium border border-teal-200 hover:bg-teal-50 transition-all flex-shrink-0"
                >
                  <Icon name={showRecoveryForm ? 'X' : 'Pencil'} size={13} className="text-teal-600" />
                  <span className="hidden sm:inline">{showRecoveryForm ? 'Annuler' : (twoFASettings?.recovery_email || twoFASettings?.recovery_phone) ? 'Modifier' : 'Configurer'}</span>
                </button>
              </div>

              {showRecoveryForm && (
                <div className="space-y-3 pt-1">
                  <div>
                    <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5 flex items-center gap-1.5">
                      <Icon name="Mail" size={12} className="text-teal-600" />
                      Email de récupération
                    </label>
                    <input
                      type="email"
                      value={recoveryEmail}
                      onChange={e => setRecoveryEmail(e?.target?.value)}
                      placeholder="email-secours@exemple.com"
                      className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                    />
                    <p className="text-[10px] text-text-secondary mt-1">
                      Peut être différent de votre email principal. Utilisé uniquement pour la récupération de compte.
                    </p>
                  </div>
                  <div>
                    <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5 flex items-center gap-1.5">
                      <Icon name="Phone" size={12} className="text-teal-600" />
                      Téléphone de récupération
                    </label>
                    <input
                      type="tel"
                      value={recoveryPhone}
                      onChange={e => setRecoveryPhone(e?.target?.value)}
                      placeholder="+225 07 00 00 00 00"
                      className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                    />
                    <p className="text-[10px] text-text-secondary mt-1">
                      Numéro de secours pour recevoir un code de récupération par SMS.
                    </p>
                  </div>

                  {recoveryError && (
                    <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex items-center gap-2">
                      <Icon name="AlertCircle" size={13} />
                      {recoveryError}
                    </p>
                  )}

                  <button
                    onClick={handleSaveRecoveryOptions}
                    disabled={recoveryLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-teal-700 disabled:opacity-60 transition-all shadow-ios-light"
                  >
                    {recoveryLoading
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Icon name="Save" size={15} color="white" />
                    }
                    <span>Enregistrer les options</span>
                  </button>
                </div>
              )}

              {recoverySuccess && (
                <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
                  <Icon name="CheckCircle" size={13} />
                  {recoverySuccess}
                </p>
              )}

              <p className="text-[10px] text-teal-700/80 flex items-start gap-1.5">
                <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0" />
                Ces informations sont chiffrées et utilisées uniquement pour vous permettre de récupérer l'accès à votre compte en cas de perte de votre méthode 2FA principale ou d'expiration de session.
              </p>
            </div>

          </div>
        </section>
      )}

      {/* ── Password Change ── */}
      <section>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-brand-earth/10 flex items-center justify-center">
            <Icon name="KeyRound" size={14} className="text-brand-earth" />
          </div>
          Changer le mot de passe
        </h3>
        <form onSubmit={handleChangePassword} className="space-y-4 bg-surface/40 rounded-2xl p-5 border border-border/40">
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Nouveau mot de passe</label>
            <input
              type="password"
              value={pwForm?.next}
              onChange={e => setPwForm(f => ({ ...f, next: e?.target?.value }))}
              placeholder="Minimum 8 caractères"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-ios-caption1 font-body-medium text-text-primary mb-1.5">Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              value={pwForm?.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e?.target?.value }))}
              placeholder="Répétez le mot de passe"
              className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
            />
          </div>
          {/* Password strength indicator */}
          {pwForm?.next && (
            <div className="space-y-1.5">
              <div className="flex gap-1">
                {[1,2,3,4]?.map(i => {
                  const strength = pwForm?.next?.length >= 12 ? 4 : pwForm?.next?.length >= 10 ? 3 : pwForm?.next?.length >= 8 ? 2 : 1;
                  return (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= strength ? (strength >= 4 ? 'bg-green-500' : strength >= 3 ? 'bg-blue-500' : strength >= 2 ? 'bg-amber-500' : 'bg-red-400') : 'bg-border'}`} />
                  );
                })}
              </div>
              <p className="text-[10px] text-text-secondary">
                {pwForm?.next?.length >= 12 ? 'Très fort' : pwForm?.next?.length >= 10 ? 'Fort' : pwForm?.next?.length >= 8 ? 'Moyen' : 'Faible'}
              </p>
            </div>
          )}
          {pwError && (
            <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100 flex items-center gap-2">
              <Icon name="AlertCircle" size={13} />
              {pwError}
            </p>
          )}
          {pwSuccess && (
            <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
              <Icon name="CheckCircle" size={13} />
              Mot de passe mis à jour avec succès.
            </p>
          )}
          <button
            type="submit"
            disabled={pwLoading}
            className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-ios-light"
          >
            {pwLoading
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <><Icon name="ShieldCheck" size={15} color="white" /><span>Mettre à jour</span></>
            }
          </button>
        </form>
      </section>
      {/* ── Active Sessions ── */}
      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-ios-callout font-body-semibold text-text-primary flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                <Icon name="Wifi" size={14} className="text-blue-600" />
              </div>
              Sessions actives
            </h3>
            <p className="text-[10px] text-text-secondary mt-1 ml-9">
              Appareils connectés, adresses IP, dernière activité et contrôle de révocation
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-body-semibold bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full border border-blue-100">
              {sessions?.length} session{sessions?.length !== 1 ? 's' : ''}
            </span>
            {trustedDevices?.length > 0 && (
              <span className="text-[10px] font-body-semibold bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full border border-amber-200 flex items-center gap-1">
                <Icon name="ShieldCheck" size={10} />
                {trustedDevices?.length} de confiance
              </span>
            )}
            <button
              onClick={() => { loadSessions(); loadTrustedDevices(); }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface border border-border/50 text-[10px] text-text-secondary hover:bg-border/20 transition-all"
            >
              <Icon name="RefreshCw" size={10} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Revoke All Confirmation */}
        {revokeAllConfirm && (
          <div className="mb-4 p-4 rounded-2xl bg-red-50/60 border border-red-200">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="ShieldOff" size={16} className="text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-ios-callout font-body-semibold text-red-700 mb-1">Révoquer toutes les autres sessions ?</p>
                <p className="text-ios-caption1 text-red-600/80 mb-3 leading-relaxed">
                  Cette action déconnectera immédiatement tous les autres appareils. Seule votre session actuelle sera conservée.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleRevokeAllSessions}
                    disabled={revokingAll}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-red-700 disabled:opacity-60 transition-all shadow-ios-light"
                  >
                    {revokingAll
                      ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <Icon name="ShieldOff" size={13} color="white" />
                    }
                    <span>Confirmer la révocation</span>
                  </button>
                  <button
                    onClick={() => setRevokeAllConfirm(false)}
                    className="px-4 py-2 bg-white text-text-secondary rounded-xl text-ios-caption1 font-body-medium border border-border/50 hover:bg-surface transition-all"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {sessionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions?.length === 0 ? (
          <div className="text-center py-8 bg-surface/40 rounded-2xl border border-border/40">
            <Icon name="WifiOff" size={24} className="text-text-secondary mx-auto mb-2" />
            <p className="text-ios-caption1 text-text-secondary">Aucune session active enregistrée.</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {sessions?.map(session => {
                const isExpanded = sessionExpanded === session?.id;
                const isCurrent = session?.is_current;
                const isTrusted = isDeviceTrusted(session);
                const devIcon = session?.device_type === 'mobile' ? 'Smartphone' : session?.device_type === 'tablet' ? 'Tablet' : 'Monitor';
                const devLabel = session?.device_type === 'mobile' ? 'Mobile' : session?.device_type === 'tablet' ? 'Tablette' : 'Ordinateur';

                // Relative time for last activity
                const lastActiveRelative = (() => {
                  if (!session?.last_active_at) return '—';
                  const diff = Date.now() - new Date(session?.last_active_at);
                  const mins = Math.floor(diff / 60000);
                  const hours = Math.floor(diff / 3600000);
                  const days = Math.floor(diff / 86400000);
                  if (mins < 1) return 'À l\'instant';
                  if (mins < 60) return `Il y a ${mins} min`;
                  if (hours < 24) return `Il y a ${hours}h`;
                  if (days < 7) return `Il y a ${days}j`;
                  return formatDate(session?.last_active_at);
                })();

                return (
                  <div
                    key={session?.id}
                    className={`rounded-2xl border transition-all overflow-hidden ${
                      isCurrent
                        ? 'bg-green-50/40 border-green-200' :'bg-white border-border/50 hover:shadow-ios-light'
                    }`}
                  >
                    {/* Main row — clickable to expand */}
                    <button
                      onClick={() => setSessionExpanded(isExpanded ? null : session?.id)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      {/* Device icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isCurrent ? 'bg-green-100' : 'bg-surface'}`}>
                        <Icon name={devIcon} size={18} className={isCurrent ? 'text-green-700' : 'text-text-secondary'} />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="text-ios-callout font-body-semibold text-text-primary truncate">
                            {session?.device_name || 'Appareil inconnu'}
                          </p>
                          {isCurrent && (
                            <span className="text-[9px] font-body-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0 border border-green-200">
                              ● Session actuelle
                            </span>
                          )}
                          <span className="text-[9px] font-body-medium bg-surface text-text-secondary px-2 py-0.5 rounded-full flex-shrink-0 border border-border/40 flex items-center gap-1">
                            <Icon name={devIcon} size={8} />
                            {devLabel}
                          </span>
                          {isTrusted && (
                            <span className="text-[9px] font-body-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0 border border-amber-200 flex items-center gap-1">
                              <Icon name="ShieldCheck" size={8} />
                              De confiance
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          {session?.ip_address && (
                            <span className="text-[10px] text-text-secondary flex items-center gap-1 font-mono">
                              <Icon name="Globe" size={9} />
                              {session?.ip_address}
                            </span>
                          )}
                          {(session?.location_city || session?.location_country) && (
                            <span className="text-[10px] text-text-secondary flex items-center gap-1">
                              <Icon name="MapPin" size={9} />
                              {[session?.location_city, session?.location_country]?.filter(Boolean)?.join(', ')}
                            </span>
                          )}
                          <span className="text-[10px] text-text-secondary flex items-center gap-1">
                            <Icon name="Clock" size={9} />
                            {lastActiveRelative}
                          </span>
                        </div>
                      </div>

                      {/* Expand chevron */}
                      <Icon
                        name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                        size={13}
                        className="text-text-secondary flex-shrink-0"
                      />
                    </button>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <div className={`px-4 pb-4 pt-0 border-t ${isCurrent ? 'border-green-200/60' : 'border-border/30'}`}>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3">
                          {/* Device type */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Type d'appareil</p>
                            <p className="text-ios-caption1 text-text-primary flex items-center gap-1">
                              <Icon name={devIcon} size={11} className="text-text-secondary" />
                              {devLabel}
                            </p>
                          </div>

                          {/* Browser / OS */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Navigateur / OS</p>
                            <p className="text-ios-caption1 text-text-primary">
                              {[session?.browser, session?.os]?.filter(Boolean)?.join(' sur ') || '—'}
                            </p>
                          </div>

                          {/* IP address */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Adresse IP</p>
                            <p className="text-ios-caption1 text-text-primary font-mono">{session?.ip_address || '—'}</p>
                          </div>

                          {/* Location */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Localisation</p>
                            <p className="text-ios-caption1 text-text-primary">
                              {[session?.location_city, session?.location_country]?.filter(Boolean)?.join(', ') || '—'}
                            </p>
                          </div>

                          {/* Last activity */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Dernière activité</p>
                            <p className="text-ios-caption1 text-text-primary font-mono">{formatDate(session?.last_active_at, true)}</p>
                          </div>

                          {/* Session started */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Démarrée le</p>
                            <p className="text-ios-caption1 text-text-primary font-mono">{formatDate(session?.created_at, true)}</p>
                          </div>
                        </div>

                        {/* Revoke button in expanded panel */}
                        {!isCurrent && (
                          <div className="mt-4 pt-3 border-t border-border/30 flex flex-wrap gap-2">
                            {/* Trust / Untrust toggle */}
                            {isTrusted ? (
                              <button
                                onClick={() => handleUntrustDevice(session)}
                                disabled={untrustingId === session?.id}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl text-ios-caption1 font-body-medium hover:bg-amber-100 disabled:opacity-50 transition-all border border-amber-200"
                              >
                                {untrustingId === session?.id
                                  ? <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                  : <Icon name="ShieldOff" size={13} className="text-amber-700" />
                                }
                                <span>Retirer la confiance</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleTrustDevice(session)}
                                disabled={trustingId === session?.id}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl text-ios-caption1 font-body-medium hover:bg-amber-100 disabled:opacity-50 transition-all border border-amber-200"
                              >
                                {trustingId === session?.id
                                  ? <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                  : <Icon name="ShieldCheck" size={13} className="text-amber-700" />
                                }
                                <span>Marquer comme de confiance</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleRevokeSession(session?.id)}
                              disabled={revokingId === session?.id}
                              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-ios-caption1 font-body-medium hover:bg-red-100 disabled:opacity-50 transition-all border border-red-100"
                            >
                              {revokingId === session?.id
                                ? <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                : <Icon name="ShieldOff" size={13} className="text-red-600" />
                              }
                              <span>Révoquer cette session</span>
                            </button>
                          </div>
                        )}
                        {/* Trust button for current session */}
                        {isCurrent && (
                          <div className="mt-4 pt-3 border-t border-green-200/60">
                            {isTrusted ? (
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-amber-700 flex items-center gap-1.5 font-body-medium">
                                  <Icon name="ShieldCheck" size={12} className="text-amber-600" />
                                  Cet appareil est de confiance — défis 2FA ignorés
                                </span>
                                <button
                                  onClick={() => handleUntrustDevice(session)}
                                  disabled={untrustingId === session?.id}
                                  className="text-[10px] text-red-500 hover:text-red-700 font-body-medium underline disabled:opacity-50 transition-colors"
                                >
                                  {untrustingId === session?.id ? 'Retrait...' : 'Retirer'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleTrustDevice(session)}
                                disabled={trustingId === session?.id}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl text-ios-caption1 font-body-medium hover:bg-amber-100 disabled:opacity-50 transition-all border border-amber-200"
                              >
                                {trustingId === session?.id
                                  ? <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                  : <Icon name="ShieldCheck" size={13} className="text-amber-700" />
                                }
                                <span>Marquer cet appareil comme de confiance</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Revoke all other sessions */}
            {sessions?.filter(s => !s?.is_current)?.length > 0 && !revokeAllConfirm && (
              <div className="mt-4 pt-4 border-t border-border/30">
                <button
                  onClick={() => setRevokeAllConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 rounded-xl text-ios-caption1 font-body-medium hover:bg-red-50 transition-all border border-red-200"
                >
                  <Icon name="ShieldOff" size={13} className="text-red-600" />
                  <span>Révoquer toutes les autres sessions ({sessions?.filter(s => !s?.is_current)?.length})</span>
                </button>
              </div>
            )}
          </>
        )}

        <p className="mt-3 text-[10px] text-text-secondary flex items-start gap-1.5">
          <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0 text-blue-500" />
          Révoquer une session déconnecte immédiatement l'appareil concerné. Cliquez sur une session pour voir les détails complets.
        </p>
        {is2FAEnabled && (
          <p className="mt-2 text-[10px] text-amber-700 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <Icon name="ShieldCheck" size={11} className="mt-0.5 flex-shrink-0 text-amber-600" />
            Les appareils marqués <strong className="font-body-semibold mx-0.5">de confiance</strong> sont exemptés des défis 2FA lors des connexions futures.
          </p>
        )}
      </section>
      {/* ── Login Activity Log ── */}
      <section>
        {/* Header */}
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h3 className="text-ios-callout font-body-semibold text-text-primary flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <Icon name="ClipboardList" size={14} className="text-amber-600" />
              </div>
              Journal d'activité de connexion
            </h3>
            <p className="text-[10px] text-text-secondary mt-1 ml-9">
              Horodatages, appareils, IP, navigateur/OS, statuts et tentatives suspectes
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Stats badges */}
            {!historyLoading && history?.length > 0 && (
              <>
                <span className="text-[10px] font-body-semibold bg-green-50 text-green-700 px-2 py-1 rounded-full border border-green-100">
                  ✓ {history?.filter(e => e?.status === 'success')?.length} succès
                </span>
                {history?.filter(e => e?.status === 'failed')?.length > 0 && (
                  <span className="text-[10px] font-body-semibold bg-red-50 text-red-600 px-2 py-1 rounded-full border border-red-100">
                    ✗ {history?.filter(e => e?.status === 'failed')?.length} échec{history?.filter(e => e?.status === 'failed')?.length > 1 ? 's' : ''}
                  </span>
                )}
                {history?.filter(e => e?._suspicious)?.length > 0 && (
                  <span className="text-[10px] font-body-semibold bg-orange-50 text-orange-700 px-2 py-1 rounded-full border border-orange-200 flex items-center gap-1">
                    <Icon name="AlertTriangle" size={10} />
                    {history?.filter(e => e?._suspicious)?.length} suspect{history?.filter(e => e?._suspicious)?.length > 1 ? 's' : ''}
                  </span>
                )}
              </>
            )}
            <button
              onClick={loadHistory}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface border border-border/50 text-[10px] text-text-secondary hover:bg-border/20 transition-all"
            >
              <Icon name="RefreshCw" size={10} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        {!historyLoading && history?.length > 0 && (
          <div className="flex gap-1.5 mb-4 flex-wrap">
            {[
              { key: 'all', label: 'Tout', icon: 'List' },
              { key: 'success', label: 'Succès', icon: 'CheckCircle' },
              { key: 'failed', label: 'Échecs', icon: 'XCircle' },
              { key: 'suspicious', label: 'Suspects', icon: 'AlertTriangle' },
            ]?.map(tab => (
              <button
                key={tab?.key}
                onClick={() => setHistoryFilter(tab?.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-body-medium transition-all border ${
                  historyFilter === tab?.key
                    ? tab?.key === 'suspicious' ? 'bg-orange-100 text-orange-700 border-orange-200'
                      : tab?.key === 'failed' ? 'bg-red-100 text-red-700 border-red-200'
                      : tab?.key === 'success'? 'bg-green-100 text-green-700 border-green-200' :'bg-brand-earth text-white border-brand-earth' :'bg-white text-text-secondary border-border/50 hover:bg-surface'
                }`}
              >
                <Icon name={tab?.icon} size={10} />
                {tab?.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-body-semibold ${
                  historyFilter === tab?.key ? 'bg-white/30' : 'bg-surface'
                }`}>
                  {tab?.key === 'all' ? history?.length
                    : tab?.key === 'success' ? history?.filter(e => e?.status === 'success')?.length
                    : tab?.key === 'failed' ? history?.filter(e => e?.status === 'failed')?.length
                    : history?.filter(e => e?._suspicious)?.length}
                </span>
              </button>
            ))}
          </div>
        )}

        {historyLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history?.length === 0 ? (
          <div className="text-center py-10 bg-surface/40 rounded-2xl border border-border/40">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Icon name="ClipboardList" size={22} className="text-amber-400" />
            </div>
            <p className="text-ios-caption1 text-text-secondary">Aucun historique de connexion disponible.</p>
            <p className="text-[10px] text-text-secondary mt-1">Les connexions futures apparaîtront ici.</p>
          </div>
        ) : (() => {
          const filtered = history?.filter(entry => {
            if (historyFilter === 'success') return entry?.status === 'success' && !entry?._suspicious;
            if (historyFilter === 'failed') return entry?.status === 'failed';
            if (historyFilter === 'suspicious') return entry?._suspicious;
            return true;
          });

          if (filtered?.length === 0) return (
            <div className="text-center py-8 bg-surface/40 rounded-2xl border border-border/40">
              <p className="text-ios-caption1 text-text-secondary">Aucune entrée pour ce filtre.</p>
            </div>
          );

          return (
            <div className="space-y-2">
              {filtered?.map(entry => {
                const isExpanded = historyExpanded === entry?.id;
                let isSuspicious = entry?._suspicious;
                const isSuccess = entry?.status === 'success';
                const isFailed = entry?.status === 'failed';

                // Relative time
                const relativeTime = (() => {
                  const diff = Date.now() - new Date(entry?.created_at);
                  const mins = Math.floor(diff / 60000);
                  const hours = Math.floor(diff / 3600000);
                  const days = Math.floor(diff / 86400000);
                  if (mins < 1) return 'À l\'instant';
                  if (mins < 60) return `Il y a ${mins} min`;
                  if (hours < 24) return `Il y a ${hours}h`;
                  if (days < 7) return `Il y a ${days}j`;
                  return formatDate(entry?.created_at);
                })();

                return (
                  <div
                    key={entry?.id}
                    className={`rounded-2xl border transition-all overflow-hidden ${
                      isSuspicious
                        ? 'border-orange-200 bg-orange-50/30'
                        : isFailed
                        ? 'border-red-100 bg-red-50/20' :'border-border/40 bg-white'
                    } hover:shadow-ios-light`}
                  >
                    {/* Main row */}
                    <button
                      onClick={() => setHistoryExpanded(isExpanded ? null : entry?.id)}
                      className="w-full flex items-center gap-3 p-3.5 text-left"
                    >
                      {/* Status icon */}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isSuspicious ? 'bg-orange-100' : isFailed ? 'bg-red-100' : 'bg-green-50'
                      }`}>
                        <Icon
                          name={isSuspicious ? 'ShieldAlert' : isFailed ? 'XCircle' : 'LogIn'}
                          size={16}
                          className={isSuspicious ? 'text-orange-600' : isFailed ? 'text-red-500' : 'text-green-600'}
                        />
                      </div>

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          {/* Device */}
                          <div className="flex items-center gap-1">
                            <Icon
                              name={entry?.device_type === 'mobile' ? 'Smartphone' : entry?.device_type === 'tablet' ? 'Tablet' : 'Monitor'}
                              size={11}
                              className="text-text-secondary flex-shrink-0"
                            />
                            <span className="text-ios-callout font-body-medium text-text-primary truncate max-w-[140px]">
                              {entry?.device_name || 'Appareil inconnu'}
                            </span>
                          </div>

                          {/* Status badge */}
                          {isSuspicious ? (
                            <span className="text-[9px] font-body-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 flex items-center gap-1 flex-shrink-0">
                              <Icon name="AlertTriangle" size={8} />
                              Suspect
                            </span>
                          ) : isFailed ? (
                            <span className="text-[9px] font-body-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-100 flex-shrink-0">
                              ✗ Échec
                            </span>
                          ) : (
                            <span className="text-[9px] font-body-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-100 flex-shrink-0">
                              ✓ Succès
                            </span>
                          )}
                        </div>

                        {/* Secondary info row */}
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Browser/OS */}
                          {(entry?.browser || entry?.os) && (
                            <span className="text-[10px] text-text-secondary flex items-center gap-1">
                              <Icon name="Globe2" size={9} />
                              {[entry?.browser, entry?.os]?.filter(Boolean)?.join(' · ')}
                            </span>
                          )}
                          {/* IP */}
                          {entry?.ip_address && (
                            <span className="text-[10px] text-text-secondary flex items-center gap-1 font-mono">
                              <Icon name="Network" size={9} />
                              {entry?.ip_address}
                            </span>
                          )}
                          {/* Location */}
                          {(entry?.location_city || entry?.location_country) && (
                            <span className="text-[10px] text-text-secondary flex items-center gap-1">
                              <Icon name="MapPin" size={9} />
                              {[entry?.location_city, entry?.location_country]?.filter(Boolean)?.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Timestamp + expand */}
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-[10px] text-text-secondary">{relativeTime}</span>
                        <Icon
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                          size={12}
                          className="text-text-secondary"
                        />
                      </div>
                    </button>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <div className={`px-4 pb-4 pt-0 border-t ${
                        isSuspicious ? 'border-orange-200/60' : isFailed ? 'border-red-100/60' : 'border-border/30'
                      }`}>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3">
                          {/* Timestamp exact */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Horodatage exact</p>
                            <p className="text-ios-caption1 text-text-primary font-mono">{formatDate(entry?.created_at, true)}</p>
                          </div>

                          {/* Status */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Statut</p>
                            <p className={`text-ios-caption1 font-body-medium ${
                              isSuspicious ? 'text-orange-700' : isFailed ? 'text-red-600' : 'text-green-700'
                            }`}>
                              {isSuspicious ? '⚠ Tentative suspecte' : isFailed ? '✗ Connexion échouée' : '✓ Connexion réussie'}
                            </p>
                          </div>

                          {/* Device type */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Type d'appareil</p>
                            <p className="text-ios-caption1 text-text-primary flex items-center gap-1">
                              <Icon
                                name={entry?.device_type === 'mobile' ? 'Smartphone' : entry?.device_type === 'tablet' ? 'Tablet' : 'Monitor'}
                                size={11}
                                className="text-text-secondary"
                              />
                              {entry?.device_type === 'mobile' ? 'Mobile' : entry?.device_type === 'tablet' ? 'Tablette' : 'Ordinateur'}
                            </p>
                          </div>

                          {/* Browser */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Navigateur / OS</p>
                            <p className="text-ios-caption1 text-text-primary">
                              {[entry?.browser, entry?.os]?.filter(Boolean)?.join(' sur ') || '—'}
                            </p>
                          </div>

                          {/* IP */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Adresse IP</p>
                            <p className="text-ios-caption1 text-text-primary font-mono">{entry?.ip_address || '—'}</p>
                          </div>

                          {/* Location */}
                          <div>
                            <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Localisation IP</p>
                            <p className="text-ios-caption1 text-text-primary">
                              {[entry?.location_city, entry?.location_country]?.filter(Boolean)?.join(', ') || '—'}
                            </p>
                          </div>

                          {/* Failure reason if present */}
                          {entry?.failure_reason && (
                            <div className="col-span-2">
                              <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">Raison de l'échec</p>
                              <p className="text-ios-caption1 text-red-600">{entry?.failure_reason}</p>
                            </div>
                          )}

                          {/* User agent if present */}
                          {entry?.user_agent && (
                            <div className="col-span-2">
                              <p className="text-[9px] font-body-semibold text-text-secondary uppercase tracking-wide mb-0.5">User-Agent</p>
                              <p className="text-[9px] text-text-secondary font-mono break-all leading-relaxed">{entry?.user_agent}</p>
                            </div>
                          )}
                        </div>

                        {/* Suspicious alert */}
                        {isSuspicious && (
                          <div className="mt-3 p-3 rounded-xl bg-orange-100/60 border border-orange-200 flex items-start gap-2">
                            <Icon name="AlertTriangle" size={13} className="text-orange-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-[10px] font-body-semibold text-orange-700 mb-0.5">Activité suspecte détectée</p>
                              <p className="text-[10px] text-orange-700/80 leading-relaxed">
                                {isFailed
                                  ? 'Tentative de connexion échouée. Si vous n\'êtes pas à l\'origine de cette tentative, changez votre mot de passe immédiatement.' :'Plusieurs tentatives échouées détectées depuis cette adresse IP. Vérifiez si vous reconnaissez cette activité.'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Footer note */}
        {!historyLoading && history?.length > 0 && (
          <p className="mt-3 text-[10px] text-text-secondary flex items-start gap-1.5">
            <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0 text-amber-500" />
            Les 50 dernières connexions sont affichées. Les tentatives suspectes sont signalées automatiquement en cas d'échecs répétés ou d'activité anormale.
          </p>
        )}
      </section>
      {/* ── Data Export ── */}
      <section>
        <h3 className="text-ios-callout font-body-semibold text-text-primary mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Icon name="Download" size={14} className="text-indigo-600" />
          </div>
          Télécharger mes données
        </h3>
        <div className="bg-surface/40 rounded-2xl p-5 border border-border/40 space-y-4">
          <p className="text-ios-caption1 text-text-secondary leading-relaxed">
            Exportez l'intégralité de vos données personnelles (profil, alertes, favoris, rendez-vous, historique d'activité, préférences) pour archivage personnel ou conformité légale (RGPD).
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExportJSON}
              disabled={exportLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-indigo-700 disabled:opacity-60 transition-all shadow-ios-light"
            >
              {exportLoading
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Icon name="FileJson" size={15} color="white" />
              }
              <span>Exporter en JSON</span>
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exportLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-600 border border-indigo-200 rounded-xl text-ios-callout font-body-medium hover:bg-indigo-50 disabled:opacity-60 transition-all shadow-ios-light"
            >
              {exportLoading
                ? <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                : <Icon name="FileSpreadsheet" size={15} className="text-indigo-600" />
              }
              <span>Exporter en CSV</span>
            </button>
          </div>
          {exportSuccess && (
            <p className="text-ios-caption1 text-green-700 bg-green-50 px-3 py-2 rounded-xl border border-green-100 flex items-center gap-2">
              <Icon name="CheckCircle" size={13} />
              {exportSuccess === 'json' ?'Fichier JSON téléchargé avec succès.' :'Fichier CSV téléchargé avec succès.'}
            </p>
          )}
          <p className="text-[10px] text-text-secondary flex items-start gap-1.5">
            <Icon name="Info" size={11} className="mt-0.5 flex-shrink-0" />
            Vos données sont exportées directement depuis notre base de données sécurisée. Aucune donnée n'est partagée avec des tiers lors de cette opération.
          </p>
        </div>
      </section>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'saved', label: 'Favoris', icon: 'Heart' },
  { key: 'alerts', label: 'Alertes', icon: 'Bell' },
  { key: 'appointments', label: 'Rendez-vous', icon: 'Calendar' },
  { key: 'preferences', label: 'Préférences', icon: 'Settings' },
  { key: 'security', label: 'Sécurité', icon: 'ShieldCheck' },
];

const UserProfilePage = () => {
  const { user, userProfile, getUserProfile, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('saved');
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/homepage');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-surface/30">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">Connexion requise</h2>
          <p className="text-ios-callout text-text-secondary mb-6">Connectez-vous pour accéder à votre profil.</p>
          <Link to="/auth" className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors shadow-ios-light">
            <Icon name="LogIn" size={16} color="white" />
            <span>Se connecter</span>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mon Profil – Kapital States</title>
        <meta name="description" content="Gérez vos propriétés sauvegardées, alertes, rendez-vous et préférences sur Kapital States." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-6">
            <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
            <Icon name="ChevronRight" size={12} />
            <span className="text-text-primary font-body-medium">Mon Profil</span>
          </div>

          {/* Profile Header */}
          <div className="bg-white rounded-3xl shadow-ios-light border border-border/30 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-gold to-brand-earth flex items-center justify-center shadow-ios-light flex-shrink-0">
                  <span className="text-white text-ios-title2 font-display font-display-semibold">
                    {(userProfile?.full_name || user?.user_metadata?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-ios-title3 font-display font-display-semibold text-brand-earth leading-tight">
                    {userProfile?.full_name || user?.user_metadata?.full_name || 'Mon Profil'}
                  </h1>
                  <p className="text-ios-callout text-text-secondary mt-0.5 font-system">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <button
                      onClick={() => !user?.email_confirmed_at ? setActiveTab('security') : undefined}
                      className={`text-[10px] font-body-semibold px-2 py-0.5 rounded-full transition-all ${user?.email_confirmed_at ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 cursor-pointer'}`}
                      title={!user?.email_confirmed_at ? 'Cliquez pour vérifier votre email' : undefined}
                    >
                      {user?.email_confirmed_at ? '✓ Email vérifié' : '⚠ Email non vérifié — cliquez pour activer'}
                    </button>
                    <span className="text-[10px] bg-brand-gold/15 text-brand-earth px-2 py-0.5 rounded-full font-body-medium capitalize">
                      {userProfile?.role || 'Client'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Link to="/account-dashboard"
                  className="flex items-center space-x-1.5 px-3.5 py-2 bg-surface rounded-xl text-ios-caption1 font-body-medium text-brand-earth hover:bg-brand-gold/10 border border-border/50 transition-all">
                  <Icon name="Settings" size={13} />
                  <span>Compte</span>
                </Link>
                <button onClick={handleSignOut}
                  className="flex items-center space-x-1.5 px-3.5 py-2 bg-red-50 rounded-xl text-ios-caption1 font-body-medium text-red-600 hover:bg-red-100 border border-red-100 transition-all">
                  <Icon name="LogOut" size={13} />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-3xl shadow-ios-light border border-border/30 overflow-hidden">
            {/* Tab Bar */}
            <div className="flex border-b border-border/40 overflow-x-auto">
              {TABS?.map(tab => (
                <button key={tab?.key} onClick={() => {
                  setActiveTab(tab?.key);
                  trackMonEspaceTabSwitch('user-profile', tab?.key);
                }}
                  className={`flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-4 px-3 text-ios-caption1 font-body-semibold transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab?.key
                      ? 'text-brand-earth border-b-2 border-brand-earth bg-brand-gold/5' :'text-text-secondary hover:text-text-primary hover:bg-surface/50'
                  }`}>
                  <Icon name={tab?.icon} size={14} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'saved' && <SavedPropertiesTab user={user} />}
              {activeTab === 'alerts' && <AlertsTab user={user} />}
              {activeTab === 'appointments' && <AppointmentsTab user={user} />}
              {activeTab === 'preferences' && <PreferencesTab user={user} userProfile={userProfile} onUpdated={getUserProfile} />}
              {activeTab === 'security' && <SecurityTab user={user} />}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserProfilePage;
